import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { taxIdCheck } from '../../utils/statutory';
import { tz, money, moneyTight, fmtDateTime, localeDate } from '../../utils/region';
import DlBtn from '../DlBtn';
import { useTranslation } from 'react-i18next';
import PenaltyCard from './PenaltyCard';
import { EoYPanel, AnnualPanel } from './AnnualTax';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { getActiveCompanyId } from '../../services/activeCompany';
import { isReadOnlyRole, isPayLinesReadOnly } from '../../utils/readOnlySeat';
import { AuditorMenu } from '../Portal';
import PartnerPicker from './PartnerPicker';
import PropertyTax from './PropertyTax';
import ExpenseBreakdownPanel from './ExpenseBreakdown';
import FilingCenter from './FilingCenter';
import TaxSummaryPanel from './TaxSummaryPanel';
import VatSummaryEmailComposer from './VatSummaryEmailComposer';
import Pnd51EmailComposer from './Pnd51EmailComposer';
import { brandOr } from '../../utils/brand';
import { pageToolbar, pageToolbarUnder, PAGE_INSET } from '../../tabPalette';   // the page toolbar's bubble + the one page gutter (report 76a2c198)
import type { InBooksRow } from '../../types';
import { ReverseEntryBtn } from '../CompanyWorkspace/reverseEntryBtn';   // batch 11 R3: a held line's "Reverse JV-…"
import PayLineHistory from './PayLineHistory';   // b13 S3 (PAY-6): a PND.1 / SSO line's History

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const r2 = (n: number) => Math.round(n * 100) / 100;

type Client = { id: string; company_name: string; client_name: string; tax_id: string; address?: string; branch_no?: string; has_vat?: number; has_wht?: number; employees?: number; sso_username?: string };

// Small tax-profile icons for the client list: VAT (registered), WHT (we run withholding), SSO (has staff).
function TaxIcons({ c }: { c: Client }) {
  const ic = (cond: any, name: string, color: string, label: string) =>
    cond ? <i className={`ti ti-${name}`} title={label} style={{ fontSize: 15, color }} /> : null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      {ic(c.has_vat, 'receipt-tax', '#2B6CB0', 'VAT')}
      {ic(c.has_wht, 'scissors', '#B45309', 'WHT')}
      {ic((c.employees || 0) > 0, 'shield-half', '#1D7C4D', 'SSO')}
    </span>
  );
}
type Line = {
  filing_no?: number;
  id: string; month: number; section: 'output' | 'input'; line_date: string; doc_no: string;
  party_name: string; party_tax_id: string; branch_head: number; branch_no: string;
  amount: number; amount0?: number; vat: number; withhold: number; total: number; confidence?: string;
  images?: { id: string; mime: string }[];
  /** Batch 11 R1 (COA-D9): the entries this line has in the books — the read-only lock chip. */
  inBooks?: InBooksRow[];
  /** Batch 11 R3 (W2-46): the document that holds the line — the entries-for kind, id and number — or null (a direct line). */
  holder?: { kind: string; id: string; docNo: string; expenseNo?: string } | null;
};
/** Batch 11 R3 (COA-D9): a line still in the books is read-only in the grid — the server refuses every write to it. */
const lineInBooks = (l: Line): boolean => !!l.inBooks?.length;
/** (W2 ship, PR5) The booked purchase note holding this line, if any — its figures, side and existence are the note's:
 *  the server refuses a figure-moving edit, a delete and a move (note_held), so the grid offers none of them. */
const noteHolder = (l: Line): { id: string; docNo: string } | null => (l.holder && l.holder.kind === 'purchase_note' ? l.holder : null);
/** Nothing that moves the line's figures is offered: in the books (R3), or a booked note's own line. */
const lineHeld = (l: Line): boolean => lineInBooks(l) || !!noteHolder(l);
/** dd/mm/yyyy of an ISO day, as the grid writes dates in its sentences. */
const dmyOf = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(String(iso || '')) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : String(iso || ''));

// The issued invoice/receipt behind a portal-booked line. Fetched with the auth header and opened
// as a blob — a bare link would 401 (the session token is not a cookie).
function DocPdfLink({ lineId }: { lineId: string }) {
  const [busy, setBusy] = useState(false);
  const open = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { const url = await api.taxLineDocPdf(lineId); window.open(url, '_blank'); }
    catch (err: any) { window.alert(err?.message || 'Could not open the document'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={open} title="Open the issued document (PDF)"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', background: 'none', cursor: busy ? 'default' : 'pointer', color: 'var(--accent)', fontSize: 11, fontWeight: 700, padding: 0 }}>
      <i className={`ti ${busy ? 'ti-loader-2' : 'ti-file-type-pdf'}`} style={{ fontSize: 16, animation: busy ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} aria-hidden />PDF
    </button>
  );
}

// Row thumbnail of the scanned invoice photo/PDF — click to view full size for verification.
function TaxThumb({ images, onOpen, docLineId }: { images?: { id: string; mime: string }[]; onOpen: () => void; docLineId?: string }) {
  const first = images && images[0];
  const isPdf = !!first && first.mime === 'application/pdf';
  const [src, setSrc] = useState('');
  useEffect(() => {
    let url = ''; let alive = true;
    if (first && !isPdf) api.taxImageBlob(first.id).then((u: string) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [first?.id]); // eslint-disable-line
  // Owner 2026-09-02: a line booked from the client's own invoicing portal has nothing scanned —
  // its evidence is the real invoice/receipt it was issued from, so link to that PDF instead of
  // leaving the cell blank.
  if (!first) {
    if (!docLineId) return null;
    return <DocPdfLink lineId={docLineId} />;
  }
  return (
    <button onClick={onOpen} title="View invoice photo" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, position: 'relative', lineHeight: 0 }}>
      {isPdf ? <span style={{ fontSize: 20 }}>📄</span>
        : src ? <img src={src} alt="" loading="lazy" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
          : <span style={{ display: 'inline-block', width: 36, height: 36, borderRadius: 4, background: 'var(--c-gray-100)' }} />}
      {images!.length > 1 && <span style={{ position: 'absolute', top: -4, right: -4, fontSize: 9, fontWeight: 700, background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '0 4px' }}>{images!.length}</span>}
    </button>
  );
}

const lbBtn: React.CSSProperties = { border: '1px solid #ffffff55', background: '#ffffff22', color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14 };
function ImageLightbox({ images, onClose }: { images: { id: string; mime: string }[]; onClose: () => void }) {
  const [i, setI] = useState(0);
  const [src, setSrc] = useState('');
  const cur = images[i];
  const isPdf = !!cur && cur.mime === 'application/pdf';
  useEffect(() => {
    let url = ''; let alive = true;
    if (cur) api.taxImageBlob(cur.id).then((u: string) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [cur?.id]); // eslint-disable-line
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []); // eslint-disable-line
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {isPdf ? (src ? <iframe title="pdf" src={src} style={{ width: '82vw', height: '84vh', border: 'none', background: '#fff', borderRadius: 6 }} /> : <div style={{ color: '#fff' }}>Loading…</div>)
          : (src ? <img src={src} alt="" style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6 }} /> : <div style={{ color: '#fff' }}>Loading…</div>)}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {images.length > 1 && <>
            <button onClick={() => setI(p => (p - 1 + images.length) % images.length)} style={lbBtn}>‹</button>
            <span style={{ color: '#fff', fontSize: 13 }}>{i + 1} / {images.length}</span>
            <button onClick={() => setI(p => (p + 1) % images.length)} style={lbBtn}>›</button>
          </>}
          <button onClick={onClose} style={lbBtn}>✕ Close</button>
        </div>
      </div>
    </div>
  );
}

// After a regenerate, if the photo shows more than one date the AI couldn't disambiguate,
// ask the operator to confirm which is the invoice date (mirrors the Telegram date picker).
function DateChoiceModal({ dates, onPick, onClose }: { dates: string[]; onPick: (iso: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 20, width: 360, maxWidth: '92vw' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('tx_regen_date_title')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 14 }}>{t('tx_regen_date_hint')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dates.map(iso => (
            <button key={iso} onClick={() => onPick(iso)} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
              📅 {iso} ({MONTHS[Number(iso.slice(5, 7)) - 1]})</button>
          ))}
        </div>
        <button onClick={onClose} style={{ marginTop: 14, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', width: '100%' }}>{t('tx_regen_date_keep')}</button>
      </div>
    </div>
  );
}

// ── Client search / picker ───────────────────────────────────────────────────
function ClientPicker({ onPick }: { onPick: (c: Client) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const h = setTimeout(() => {
      setLoading(true);
      api.taxSearchClients(q, 1).then((d: any) => { setRows(d.rows || []); setTotal(d.total || 0); }).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px', width: '100%' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{t('tx_pick_client')}</div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('tx_search_client')}
          style={{ width: '100%', padding: '11px 12px 11px 36px', fontSize: 14, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : rows.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('tx_no_client')}</div>
          : rows.map(c => (
            <div key={c.id} onClick={() => onPick(c)} style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{c.company_name || c.client_name}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <TaxIcons c={c} />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.tax_id || '—'}</span>
              </span>
            </div>
          ))}
      </div>
      {total > rows.length && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 8, textAlign: 'center' }}>{t('tx_more_results', { n: total - rows.length })}</div>}
    </div>
  );
}

// A numeric cell that lets you type decimals. A plain controlled `value={number}` breaks decimal
// entry: each keystroke is coerced with Number(), so typing "1392." becomes 1392 and the dot is
// dropped on the re-render. This keeps the RAW text while you're editing (so "1392." / "1392.5"
// survive), still reports the parsed number up for live totals, and mirrors external value changes
// (OCR / Re-OCR / regenerate) whenever the field isn't focused. Normalizes on blur.
function NumCell({ value, onValue, onBlurSave, style, title, readOnly }: {
  value: number; onValue: (n: number) => void; onBlurSave: () => void; style: React.CSSProperties; title?: string;
  /** Batch 11 R3: a line in the books — shown, not edited. */
  readOnly?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [txt, setTxt] = useState(value ? String(value) : '');
  useEffect(() => { if (!focused) setTxt(value ? String(value) : ''); }, [value, focused]);
  if (readOnly) return <input value={value ? String(value) : ''} readOnly title={title} style={style} />;
  return (
    <input value={txt} inputMode="decimal" title={title} style={style}
      onFocus={() => setFocused(true)}
      onChange={e => { const s = e.target.value; if (s !== '' && !/^-?\d*\.?\d*$/.test(s)) return; setTxt(s); onValue(Number(s) || 0); }}
      onBlur={() => { setFocused(false); const n = Number(txt) || 0; setTxt(n ? String(n) : ''); onBlurSave(); }} />
  );
}

// ── One section's editable table (output = sales, input = purchase) ──────────
const cell: React.CSSProperties = { fontSize: 12.5, padding: '4px 6px', borderRight: '1px solid var(--border)', verticalAlign: 'middle' };
const inp: React.CSSProperties = { width: '100%', border: '1px solid transparent', background: 'transparent', fontSize: 12.5, padding: '4px 5px', borderRadius: 5, outline: 'none', boxSizing: 'border-box', color: 'var(--text-primary)' };
// 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below where it was 7, on one 16px line, on white, over a 1px --border-strong
// line where it was 2px — a 37px band, as every list's is. The lines down between the columns stay. The payroll
// tables' h and the filing grid's th further down take the same look at their own sizes.
// The gap under the text is 5px now, where it was 10, so it sits nearer its grey line and the band is 32px (report
// c151d269: "Bring the heading text 5 pixels closer to the grey line below. Do this for every page." — see Active
// Jobs' th). Every heading in this module went with it: the payroll tables' h, the filing grid's th, Property Tax's
// and the expense breakdown's, those last two by 11 → 6px because they draw their line as an inset shadow.
const th: React.CSSProperties = { fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 6px 5px', lineHeight: '16px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const warnInp: React.CSSProperties = { border: '1px solid #F59E0B', background: '#FFFBEB' };

// Required fields a line must have to be complete — mirrors the server's missingFields().
// Used to amber-highlight exactly the cells the user still needs to fill in by hand
// (handwritten / partially-readable invoices the OCR couldn't fully read).
function missOf(l: Line): Set<string> {
  const s = new Set<string>();
  if (!String(l.line_date || '').trim()) s.add('line_date');
  if (!String(l.doc_no || '').trim()) s.add('doc_no');
  if (!String(l.party_name || '').trim()) s.add('party_name');
  if (taxIdCheck(l.party_tax_id || '').reason === 'empty') s.add('party_tax_id');   // utils/statutory.ts
  if (!(Number(l.amount) > 0) && !(Number(l.amount0) > 0)) s.add('amount');
  return s;
}

function SectionTable({ title, accent, section, lines, hasVat, clientId, onPatchSave, onChange, onBlurSave, onAdd, onDelete, onUpload, uploading, onReview, onMoveAll, onViewImages, onMoveSelected, onRegenerate, regeneratingId, filingOptions, onMoveToFiling }: {
  title: string; accent: string; section: 'output' | 'input'; lines: Line[]; hasVat: boolean; clientId: string;
  onPatchSave: (id: string, patch: Partial<Line> & { partnerId?: string }) => void;
  onChange: (id: string, patch: Partial<Line>) => void; onBlurSave: (id: string) => void; onAdd: () => void; onDelete: (id: string) => void;
  onUpload: (files: FileList) => void; uploading: boolean; onReview: (id: string) => void; onMoveAll: () => void;
  onViewImages: (imgs: { id: string; mime: string }[]) => void; onMoveSelected: (ids: string[]) => void;
  filingOptions?: { no: number; label: string }[]; onMoveToFiling?: (ids: string[], no: number) => void;
  onRegenerate: (id: string) => void; regeneratingId: string;
}) {
  const moveFnRef = React.useRef<HTMLSelectElement | null>(null);
  const { t } = useTranslation();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const toggleSel = (id: string) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Batch 11 R3: a line in the books cannot be ticked for "move selected" (the server refuses to flip it), and "move all"
  // is disabled with the sentence while any line of the section is in the books.
  const movable = lines.filter(l => !lineHeld(l));
  const anyInBooks = movable.length !== lines.length;
  const visibleSel = movable.filter(l => selected.has(l.id)).map(l => l.id);
  const allChecked = movable.length > 0 && visibleSel.length === movable.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(movable.map(l => l.id)));
  const me = useStore(s => s.me);
  // Staff who write the books get "Reverse JV-…" on a line a document holds; everyone else reads the sentence.
  const reverses = !!me && !isReadOnlyRole(me.role) && (me.modules || []).includes('accounting_books');
  const tot = lines.reduce((a, l) => ({ amount: a.amount + l.amount, amount0: a.amount0 + (Number(l.amount0) || 0), vat: a.vat + l.vat, withhold: a.withhold + l.withhold, total: a.total + l.total }), { amount: 0, amount0: 0, vat: 0, withhold: 0, total: 0 });
  // The 0%/second-base column is normally a VAT-only affair, but it is ALSO the slot that says
  // "the WHT applies to only part of this sale" — which is how our own firms' receipts book
  // (ownFirmBooking). A non-VAT client that has such a row must still be able to SEE it, or the
  // Amount column and the Total column disagree on screen with nothing explaining the gap.
  const showA0 = hasVat || tot.amount0 > 0.005;
  const numInp = { ...inp, textAlign: 'right' as const };
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: accent }} />
        <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{title}</div>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1130 }}>
          <thead>
            <tr style={{ background: 'var(--c-gray-50)' }}>
              <th rowSpan={2} style={{ ...th, width: 28, textAlign: 'center' }}><input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all" /></th>
              <th rowSpan={2} style={{ ...th, width: 34 }}>{t('tx_no')}</th><th rowSpan={2} style={{ ...th, width: 110 }}>{t('tx_date')}</th>
              <th rowSpan={2} style={{ ...th, width: 120 }}>{t('tx_doc')}</th><th rowSpan={2} style={{ ...th, minWidth: 180 }}>{t('tx_party')}</th>
              <th rowSpan={2} style={{ ...th, width: 130 }}>{t('tx_party_tax')}</th>
              <th colSpan={2} style={{ ...th, textAlign: 'center' }}>{t('tx_branch')}</th>
              <th rowSpan={2} style={{ ...th, width: 100, textAlign: 'right' }}>{t('tx_amount')}</th>
              {hasVat && <th rowSpan={2} style={{ ...th, width: 90, textAlign: 'right' }}>{t('tx_vat')}</th>}
              {showA0 && <th rowSpan={2} style={{ ...th, width: 95, textAlign: 'right' }}>{t(hasVat ? 'tx_amount0' : 'tx_amount_nowht')}</th>}
              <th rowSpan={2} style={{ ...th, width: 90, textAlign: 'right' }}>{t('tx_withhold')}</th>
              <th rowSpan={2} style={{ ...th, width: 100, textAlign: 'right' }}>{t('tx_total')}</th>
              <th rowSpan={2} style={{ ...th, width: 104, textAlign: 'center' }}>{t('tx_photo')}</th>
              <th rowSpan={2} style={{ ...th, width: 30, borderRight: 'none' }} />
            </tr>
            <tr style={{ background: 'var(--c-gray-50)' }}>
              <th style={{ ...th, width: 80, textAlign: 'center' }}>{t('tx_head_office')}</th>
              <th style={{ ...th, width: 80, textAlign: 'center' }}>{t('tx_branch')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const miss = missOf(l);
              const flagged = miss.size > 0 || (!!l.confidence && l.confidence !== 'high');
              const inclBase = (l as any).vat_incl_base as number | undefined;
              const locked = lineHeld(l);
              // (Batch 11 W4 P9, LB-37) A line a supplier bill or an approved scan holds: its FIGURES are the record's — the
              // server refuses a change to them (409 bill_held), so those cells are read-only here and say why.
              const billHeld = !locked && (l.holder?.kind === 'payable' || l.holder?.kind === 'expense_upload');
              const figLocked = locked || billHeld;
              const lockNos = (l.inBooks || []).map(x => x.docNo).join(', ');
              const heldNote = lineInBooks(l) ? null : noteHolder(l);
              // (W2 ship, S4) An entry posted in a period closed since is not reversed: its own sentence, no Reverse.
              const shut = (l.inBooks || []).find(x => !!x.closed && !x.reversalDraft);
              // (W3 ship review) The entry "Reverse JV-…" opens on: a bill's PAYMENT settlement (W3-31) is reversed on its
              // statement line's entries panel, which lists it — the bill's own ('payable') lists its recognition only.
              const focus = (l.inBooks || [])[0];
              const viaLine = focus?.role === 'settlement' && focus?.lineId ? String(focus.lineId) : '';
              return (
              <React.Fragment key={l.id}>
              <tr title={(l as any).calc_error ? t('tx_calc_bad_tip') : undefined} style={{ borderBottom: '1px solid var(--border)', background: (l as any).calc_error ? '#FDE8E8' : regeneratingId === l.id ? '#DBEAFE' : selected.has(l.id) ? '#EFF6FF' : l.confidence === 'low' ? '#FFF7ED' : (l.confidence === 'medium' || miss.size > 0) ? '#FFFCF2' : undefined }}>
                <td style={{ ...cell, textAlign: 'center' }}><input type="checkbox" checked={!locked && selected.has(l.id)} disabled={locked} title={locked ? (heldNote ? t('tx_ll_note_held', { noteNo: heldNote.docNo }) : t('tx_ll_locked', { docNos: lockNos })) : undefined} onChange={() => toggleSel(l.id)} /></td>
                <td style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  {flagged
                    ? <button onClick={() => onReview(l.id)} title={miss.size > 0 ? t('tx_fill_manual') : t('tx_ocr_review')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#D97706', padding: 0, lineHeight: 1 }}><i className="ti ti-alert-triangle" /></button>
                    : i + 1}
                </td>
                <td style={cell}><input type="date" value={l.line_date} readOnly={locked} onChange={e => onChange(l.id, { line_date: e.target.value })} onBlur={() => { if (!locked) onBlurSave(l.id); }} style={miss.has('line_date') ? { ...inp, ...warnInp } : inp} /></td>
                <td style={cell}><input value={l.doc_no} readOnly={locked} onChange={e => onChange(l.id, { doc_no: e.target.value })} onBlur={() => { if (!locked) onBlurSave(l.id); }} style={miss.has('doc_no') ? { ...inp, ...warnInp } : inp} />
                  {/* Batch 11 R1 (COA-D9): posted in the books — a read-only chip naming the entries in its title.
                      The line's own controls keep exactly the state they had; R3 decides what they may do. */}
                  {!!l.inBooks?.length && (() => {
                    // (W1 ship) An entry recorded by hand for this line, posted before the line changed: the chip says so
                    // (amber, and why in its title) — the figures typed by hand do not follow the line. Read-only.
                    const changed = l.inBooks.filter(x => x.handChanged);
                    const tip = t('tx_ib_tip', { docNos: l.inBooks.map(x => x.docNo).join(', ') })
                      + (changed.length ? ` ${t('tx_ib_hand_changed', { docNos: changed.map(x => x.docNo).join(', ') })}` : '');
                    return (
                      <span data-inbooks-chip={l.id} data-inbooks-hand-changed={changed.length ? changed.map(x => x.docNo).join(',') : undefined} title={tip}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3, padding: '1px 6px', borderRadius: 999, background: changed.length ? '#FFFBEB' : '#EFF6FF', border: `1px solid ${changed.length ? '#FCD34D' : '#BFDBFE'}`, color: changed.length ? '#92400E' : '#1E3A8A', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        <i className={changed.length ? 'ti ti-alert-triangle' : 'ti ti-lock'} aria-hidden style={{ fontSize: 11 }} />{t('tx_ib_chip')}
                      </span>
                    );
                  })()}
                  {/* Batch 11 R3 (W2-46): the way on. A line a document holds → that document's entries panel on the
                      entry ("Reverse JV-…", staff who write the books); a direct line no document holds → the sentence
                      naming the entry and Journal entries (tax_line is not a document kind). */}
                  {heldNote && <div data-ll-note={l.id} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>{t('tx_ll_note_held', { noteNo: heldNote.docNo })}</div>}
                  {billHeld && l.holder && <div data-ll-bill-held={l.id} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                    {t(l.holder.kind === 'payable' ? 'tx_ll_bill_held' : 'tx_ll_scan_held', { docNo: l.holder.docNo })}</div>}
                  {/* The expense record's number beside the supplier's (2026-11-02, Automatic Expense Number), by join, never
                      in the doc_no box the return reads. Not repeated where the sentence above already names it. */}
                  {!!l.holder?.expenseNo && !(billHeld && l.holder.docNo === l.holder.expenseNo) && (
                    <div data-ll-expense-no={l.id} title={t('aw_er_col_no')} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{l.holder.expenseNo}</div>
                  )}
                  {locked && !heldNote && shut && <div data-ll-closed={l.id} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>{t('tx_ll_closed', { docNos: lockNos, docNo: shut.docNo, from: dmyOf(shut.closed!.start), to: dmyOf(shut.closed!.end) })}</div>}
                  {locked && !heldNote && !shut && (l.holder
                    ? (reverses
                        ? <div style={{ marginTop: 3 }}><ReverseEntryBtn ctx={{ clientId }} kind={viaLine ? 'bank_line' : l.holder.kind} id={viaLine || l.holder.id}
                            docNo={l.holder.docNo} entry={(l.inBooks || [])[0]}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: '1px solid var(--border-strong)', borderRadius: 6, padding: '1px 6px', background: 'var(--bg-surface)', color: 'var(--accent)', cursor: 'pointer', fontSize: 10, fontWeight: 700 }} /></div>
                        : <div data-ll-line={l.id} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>{t('tx_ll_locked', { docNos: lockNos })}</div>)
                    : <div data-ll-direct={l.id} style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>{t('tx_ll_direct', { docNos: lockNos })}</div>)}
                </td>
                <td style={cell}>
                  {/* Partner DB P3: picking fills name + tax ID + head/branch in ONE saved patch; free
                      typing still works (plenty of counterparties are in no registry). A picked
                      partner's branchNo decides which of the two branch columns is set. */}
                  {locked ? <input value={l.party_name} readOnly style={inp} /> : <PartnerPicker
                    value={l.party_name}
                    clientId={clientId}
                    inputStyle={miss.has('party_name') ? warnInp : undefined}
                    onCommit={v => onPatchSave(l.id, { party_name: v })}
                    onPick={p => onPatchSave(l.id, {
                      party_name: p.name, party_tax_id: p.taxId, partnerId: p.partnerId,
                      branch_no: p.branchNo, branch_head: p.branchNo === '00000' ? 1 : 0,
                    })}
                  />}
                </td>
                <td style={cell}><input value={l.party_tax_id} readOnly={locked} onChange={e => onChange(l.id, { party_tax_id: e.target.value })} onBlur={() => { if (!locked) onBlurSave(l.id); }} style={miss.has('party_tax_id') ? { ...inp, ...warnInp } : inp} /></td>
                <td style={cell}><input value={l.branch_head ? (l.branch_no || '') : ''} readOnly={locked} onChange={e => onChange(l.id, { branch_no: e.target.value, branch_head: 1 })} onBlur={() => { if (!locked) onBlurSave(l.id); }} style={{ ...inp, textAlign: 'center' }} placeholder="00000" /></td>
                <td style={cell}><input value={!l.branch_head ? (l.branch_no || '') : ''} readOnly={locked} onChange={e => onChange(l.id, { branch_no: e.target.value, branch_head: 0 })} onBlur={() => { if (!locked) onBlurSave(l.id); }} style={{ ...inp, textAlign: 'center' }} placeholder="-" /></td>
                <td style={cell}><NumCell readOnly={figLocked} value={Number(l.amount) || 0} onValue={a => onChange(l.id, { amount: a, total: r2(a + (Number(l.amount0) || 0) + (Number(l.vat) || 0)) })} onBlurSave={() => onBlurSave(l.id)} style={miss.has('amount') ? { ...numInp, ...warnInp } : numInp} /></td>
                {hasVat && <td style={cell}><NumCell readOnly={figLocked} value={Number(l.vat) || 0} onValue={v => onChange(l.id, { vat: v, total: r2((Number(l.amount) || 0) + (Number(l.amount0) || 0) + v) })} onBlurSave={() => onBlurSave(l.id)} style={numInp} title={t('tx_vat_hint')} /></td>}
                {showA0 && <td style={cell}><NumCell readOnly={figLocked} value={Number(l.amount0) || 0} onValue={a0 => onChange(l.id, { amount0: a0, total: r2((Number(l.amount) || 0) + a0 + (Number(l.vat) || 0)) })} onBlurSave={() => onBlurSave(l.id)} style={numInp} title={t('tx_amount0_hint')} /></td>}
                <td style={cell}><NumCell readOnly={figLocked} value={Number(l.withhold) || 0} onValue={w => onChange(l.id, { withhold: w })} onBlurSave={() => onBlurSave(l.id)} style={numInp} /></td>
                <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{money(l.total)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <TaxThumb images={l.images} onOpen={() => onViewImages(l.images || [])}
                      docLineId={((l as any).receipt_id || (l as any).invoice_id) ? l.id : undefined} />
                    {l.images && l.images.length > 0 && !locked && (
                      <button onClick={() => onRegenerate(l.id)} disabled={!!regeneratingId} title={t('tx_regen')}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, border: `1px solid ${regeneratingId === l.id ? 'var(--accent)' : 'var(--border-strong)'}`, borderRadius: 6, padding: '2px 6px', background: regeneratingId === l.id ? 'var(--accent)' : 'var(--bg-surface)', color: regeneratingId === l.id ? '#fff' : 'var(--accent)', cursor: regeneratingId ? 'default' : 'pointer', fontSize: 9.5, fontWeight: 800, lineHeight: 1.4, whiteSpace: 'nowrap', opacity: regeneratingId && regeneratingId !== l.id ? 0.4 : 1 }}>
                        <i className="ti ti-refresh" style={{ fontSize: 12, display: 'inline-block', animation: regeneratingId === l.id ? 'spin 0.7s linear infinite' : undefined }} />{regeneratingId === l.id ? t('tx_regen_short_loading') : t('tx_regen_short')}</button>
                    )}
                  </div>
                </td>
                <td style={{ ...cell, textAlign: 'center', borderRight: 'none' }}>{!locked && <button onClick={() => onDelete(l.id)} title={t('delete')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>}</td>
              </tr>
              {/* The one VAT failure the server can name: `amount` is the invoice's VAT-INCLUSIVE total,
                  not the 7% base. Saying so — and offering the base — beats leaving the operator to
                  work out why "VAT does not match". Correcting sends the row through the normal save,
                  so the server re-derives total and clears the flag itself. */}
              {inclBase != null && !locked && (
                <tr>
                  <td colSpan={hasVat ? 16 : 14} style={{ padding: '7px 12px', background: '#FEF3C7', borderBottom: '1px solid var(--border)', fontSize: 11.5, color: '#92400E' }}>
                    <i className="ti ti-bulb" style={{ marginRight: 6 }} />
                    {t('tx_vat_incl_hint').replace('{gross}', money(l.amount)).replace('{base}', money(inclBase))}
                    <button onClick={() => { onChange(l.id, { amount: inclBase } as any); setTimeout(() => onBlurSave(l.id), 0); }}
                      style={{ marginLeft: 10, border: '1px solid #B45309', borderRadius: 6, padding: '2px 10px', background: '#fff', color: '#B45309', cursor: 'pointer', fontSize: 11, fontWeight: 800 }}>
                      {t('tx_vat_incl_fix')}
                    </button>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
            <tr style={{ background: 'var(--c-gray-50)', fontWeight: 700 }}>
              <td colSpan={8} style={{ ...cell, textAlign: 'right' }}>{t('tx_total_amount')}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{money(tot.amount)}</td>
              {hasVat && <td style={{ ...cell, textAlign: 'right' }}>{money(tot.vat)}</td>}
              {showA0 && <td style={{ ...cell, textAlign: 'right' }}>{money(tot.amount0)}</td>}
              <td style={{ ...cell, textAlign: 'right' }}>{money(tot.withhold)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{money(tot.total)}</td>
              <td style={cell} />
              <td style={{ ...cell, borderRight: 'none' }} />
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={onAdd} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${accent}`, background: 'transparent', color: accent, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>＋ {t('tx_add_line')}</button>
        <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }}
          onChange={e => { if (e.target.files && e.target.files.length) onUpload(e.target.files); e.target.value = ''; }} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: 'none', background: uploading ? 'var(--c-gray-200)' : accent, color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: uploading ? 'default' : 'pointer' }}>
          <i className={`ti ti-${uploading ? 'loader-2' : 'camera'}`} style={{ fontSize: 14 }} />{uploading ? t('tx_ocr_reading') : t('tx_ocr_upload')}</button>
        {lines.length > 0 && (visibleSel.length > 0
          ? <>
            {filingOptions && onMoveToFiling && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
                <select ref={el => { (moveFnRef as any).current = el; }} defaultValue={String(filingOptions[filingOptions.length - 1]?.no ?? 0)}
                  style={{ padding: '5px 8px', fontSize: 12, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
                  {filingOptions.map(o => <option key={o.no} value={o.no}>{o.label}</option>)}
                </select>
                <button onClick={() => { const el = (moveFnRef as any).current as HTMLSelectElement | null; onMoveToFiling(visibleSel, Number(el?.value || 0)); setSelected(new Set()); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', fontSize: 12, fontWeight: 700, borderRadius: 7, border: '1px solid #7C3AED', background: 'transparent', color: '#7C3AED', cursor: 'pointer' }}>
                  <i className="ti ti-stack-push" style={{ fontSize: 13 }} />{t('tx_move_filing', { n: visibleSel.length })}
                </button>
              </span>
            )}
            <button onClick={() => { onMoveSelected(visibleSel); setSelected(new Set()); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: `1px solid ${accent}`, background: accent, color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
              <i className="ti ti-transfer" style={{ fontSize: 14 }} />{t(section === 'output' ? 'tx_move_sel_to_input' : 'tx_move_sel_to_output', { n: visibleSel.length })}</button>
          </>
          : <>
            <button onClick={onMoveAll} disabled={anyInBooks} data-ll-move-all={anyInBooks ? 'disabled' : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: anyInBooks ? 'not-allowed' : 'pointer', opacity: anyInBooks ? 0.5 : 1 }}>
              <i className="ti ti-transfer" style={{ fontSize: 14 }} />{section === 'output' ? t('tx_move_to_input') : t('tx_move_to_output')}</button>
            {anyInBooks && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('tx_ll_move_all_disabled')}</span>}
          </>)}
        {/* Form 50 Tawi is NOT here: the WHT certificate is issued from the withholding ledger, so it
            lives on the PND.3 / PND.53 pages (WhtPanel), not on the VAT filing. */}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('tx_ocr_hint')}</span>
      </div>
    </div>
  );
}


// ── Carry-forward VAT credit card (May's spec 2026-07-29): brought / net / payable-or-carry,
//    plus the one-time OPENING balance editor (期初留抵) that starts the chain. ──
// Row-level entry point in the Filing Center: filing 40 companies means switching client 40 times
// otherwise. A single icon per row (green once both documents are in) opens the same upload UI in a
// popover — the editable grid stays a grid, with no extra buttons competing for the row.
function FilingDocsRowButton({ clientId, filing, year, month, clientName }: {
  clientId: string; filing: string; year: number; month: number; clientName: string;
}) {
  const { t } = useTranslation();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const [state, setState] = useState<{ receipt: number; form: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const refresh = () => api.filingDocsList(clientId, filing, year, month)
    .then((d: any) => setState({ receipt: (d.docs?.receipt || []).length, form: (d.docs?.form || []).length }))
    .catch(() => setState({ receipt: 0, form: 0 }));
  useEffect(() => { if (clientId) refresh(); }, [clientId, filing, year, month]);   // eslint-disable-line
  useEffect(() => {
    if (!at) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setAt(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAt(null); };
    const close = () => setAt(null);
    document.addEventListener('mousedown', away); window.addEventListener('keydown', esc); window.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc); window.removeEventListener('scroll', close, true); };
  }, [at]);
  const both = !!state && state.receipt > 0 && state.form > 0;
  const some = !!state && (state.receipt > 0 || state.form > 0);
  if (!clientId) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  return (
    <>
      <button ref={btnRef} title={t('fd_title')}
        onClick={() => { if (at) { setAt(null); return; } const r = btnRef.current?.getBoundingClientRect(); setAt(r ? { x: r.right, y: r.bottom } : { x: 0, y: 0 }); }}
        style={{ border: 'none', background: 'none', cursor: 'pointer', color: both ? '#15803D' : some ? '#B45309' : 'var(--text-tertiary)' }}>
        <i className={`ti ti-${both ? 'file-check' : 'paperclip'}`} style={{ fontSize: 15 }} />
      </button>
      {at && (
        <div ref={boxRef} onClick={e => e.stopPropagation()}
          style={{ position: 'fixed', top: at.y + 6, left: Math.max(8, at.x - 520), width: 520, maxWidth: '94vw', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-md)', zIndex: 10075, padding: 12, textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>{clientName}</div>
          <FilingDocsCard clientId={clientId} filing={filing} year={year} month={month} monthName={MONTHS[month - 1]} onChanged={refresh} embedded />
          <PenaltyCard clientId={clientId} kind={filing} year={year} month={month} />
        </div>
      )}
    </>
  );
}

// ── Filed-return documents for one client-month ──────────────────────────────
// The two pieces of official evidence a submitted return produces: the Revenue Department payment
// receipt and the filed form itself. Uploading renames the file to the firm's convention
// (PP30_Receipt_2026_Jan.pdf) and files it in the client's folder — the same client_docs archive
// File Library shows — so nobody has to remember where to put it or what to call it.
export function FilingDocsCard({ clientId, filing, year, month, monthName, onChanged, embedded, filingNo = 0 }: {
  clientId: string; filing: string; year: number; month: number; monthName: string;
  onChanged?: () => void; embedded?: boolean; filingNo?: number;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const slotRef = useRef<string>('receipt');   // slot names come from the server, not a fixed union
  const load = () => api.filingDocsList(clientId, filing, year, month, filingNo).then(setData).catch(() => {});
  useEffect(() => { setData(null); load(); }, [clientId, filing, year, month, filingNo]);   // eslint-disable-line
  const pick = (slot: string) => { slotRef.current = slot; inputRef.current?.click(); };
  // Every picked file is ADDED to the slot, one upload each in the order picked (report dca12732) —
  // the list refills after each, so a long batch shows its progress. A file that fails is named and
  // skipped; the rest still go in.
  const onFile = async (e: any) => {
    const files = Array.from(e.target.files || []) as File[]; e.target.value = '';
    if (!files.length) return;
    const slot = slotRef.current;
    setBusy(slot);
    const failed: string[] = [];
    let added = 0, autoFiled = false;
    try {
      for (const f of files) {
        try {
          const r: any = await api.filingDocsUpload(clientId, filing, year, month, slot, f, filingNo);
          added++; if (r?.autoFiled) autoFiled = true;
          await load();
        } catch (er: any) { failed.push(`${f.name}: ${er.message}`); }
      }
      if (added) onChanged?.();
      if (failed.length) alert(failed.join('\n'));
      if (autoFiled) alert(t('fd_auto_filed'));
    } catch (er: any) { alert(er.message); } finally { setBusy(''); }
  };
  const del = async (id: string) => {
    if (!window.confirm(t('fd_delete_confirm'))) return;
    try { await api.filingDocsDelete(id); await load(); onChanged?.(); } catch (er: any) { alert(er.message); }
  };
  const fileRow = (d: any, removable = true) => (
    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, padding: '3px 0' }}>
      <i className="ti ti-file-type-pdf" style={{ color: '#B42318' }} />
      {/* Buttons, not links: the file is behind requireAuth, so it needs a ticket fetched first. */}
      <button onClick={() => { api.openFilingDoc(d.id, true).catch((e: any) => alert(e.message)); }}
        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, textAlign: 'left', font: 'inherit' }}>{d.filename}</button>
      <button onClick={() => { api.openFilingDoc(d.id).catch((e: any) => alert(e.message)); }}
        title={t('fd_download')} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-download" /></button>
      <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{Math.round((d.size || 0) / 1024)} KB · {d.created_by_name || ''}</span>
      {removable && <button onClick={() => del(d.id)} title={t('delete_short')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-trash" /></button>}
    </div>
  );
  const slot = (key: string, label: string) => {
    const list = (data?.docs || {})[key] || [];
    return (
      <div style={{ flex: 1, minWidth: 250, border: '1px dashed var(--border-strong)', borderRadius: 8, padding: '9px 11px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{label}</span>
          {list.length > 0 && <i className="ti ti-circle-check" style={{ color: '#15803D', fontSize: 14 }} />}
          <button onClick={() => pick(key)} disabled={!!busy}
            style={{ marginLeft: 'auto', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', borderRadius: 7, padding: '4px 11px', fontSize: 11.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy === key ? <i className="ti ti-loader-2" style={{ animation: 'spin .7s linear infinite', display: 'inline-block' }} /> : <><i className="ti ti-upload" /> {list.length ? t('fd_replace') : t('fd_upload')}</>}
          </button>
        </div>
        {list.length === 0
          ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('fd_none')}</div>
          : list.map((d: any) => fileRow(d))}
      </div>
    );
  };
  const legacy = data?.fromEmail || [];
  return (
    <div style={embedded ? {} : { border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, background: 'var(--bg-surface)' }}>
      <input ref={inputRef} type="file" multiple accept=".pdf,image/*" style={{ display: 'none' }} onChange={onFile} />
      <div style={{ fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {/* An annual return has no month, so it is titled by the year alone rather than "2026 2026". */}
        <i className="ti ti-file-certificate" style={{ color: '#0E7C66' }} />{t('fd_title')} — {monthName ? `${monthName} ${year}` : year}
      </div>
      {/* Which slots exist depends on the return: VAT is filed as receipt + form; the payroll returns
          add the employee schedule. The server decides (filingDocs SLOTS_FOR) and the card renders
          whatever came back, so adding a slot never needs a matching edit here. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* Label by FILING + slot first ("PND.1 Return Form", "Social Security Office payment
            receipt"), generic slot label as the fallback — the generic fd_form said "PP30 VAT
            return form" on every filing kind, which is nonsense on the PND.1 and SSO cards. */}
        {Object.keys(data?.docs || { receipt: [], form: [] }).map(k =>
          <React.Fragment key={k}>{slot(k, t(`fd_${filing}_${k}`, { defaultValue: t(`fd_${k}`) }))}</React.Fragment>)}
      </div>
      {legacy.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 3 }}>{t('fd_from_email')}</div>
          {legacy.map((d: any) => fileRow(d, false))}
        </div>
      )}
    </div>
  );
}

function VatCreditCard({ entry, additional, opening, monthName, hasChain, onSaveOpening, onEmail }: {
  entry: { brought: number; net: number; payable: number; carry: number } | null;
  // Every additional ภ.พ.30 of this month, each on its OWN figures — the carried-forward credit
  // belongs to the normal return and is never spent here (owner-approved 2026-08-08).
  additional?: Array<{ filingNo: number; outVat: number; inVat: number; net: number; payable: number; creditUsed: number; creditGenerated: number }>;
  opening: { amount: number; ym: string };
  monthName: string; hasChain: boolean;
  onSaveOpening: (amount: number, ym: string) => Promise<void>;
  onEmail?: () => void;   // "Email to Client" — the Monthly VAT Summary composer (manual only)
}) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState(false);
  const [amt, setAmt] = useState('');
  const [ym, setYm] = useState('');
  const [busy, setBusy] = useState(false);
  const start = () => { setAmt(String(opening.amount || '')); setYm(opening.ym || ''); setEdit(true); };
  const save = async () => {
    if (busy) return;
    const n = Number(String(amt).replace(/,/g, '').trim() || '0');
    if (!Number.isFinite(n) || n < 0) { alert(t('tx_credit_open_bad')); return; }
    setBusy(true);
    try { await onSaveOpening(n, ym); setEdit(false); } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };
  const lbl: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 700 };
  const val: React.CSSProperties = { fontSize: 15, fontWeight: 800, textAlign: 'right' };
  const adds = additional || [];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, background: 'var(--c-gray-50)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="ti ti-arrow-forward-up" style={{ color: '#2B6CB0' }} />{t('tx_credit_title')} — {monthName}
        {!!adds.length && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>· {t('tx_credit_normal')}</span>}
      </div>
      {entry ? (<>
        <div style={box}><span style={lbl}>{t('tx_credit_bf')}</span><span style={val}>{money(entry.brought)}</span></div>
        <div style={box}><span style={lbl}>{t('tx_credit_net')}</span><span style={val}>{money(entry.net)}</span></div>
        {entry.payable > 0
          ? <div style={box}><span style={{ ...lbl, color: '#B45309' }}>{t('tx_credit_payable')}</span><span style={{ ...val, color: '#B45309' }}>{money(entry.payable)}</span></div>
          : <div style={box}><span style={{ ...lbl, color: '#047857' }}>{t('tx_credit_cf')}</span><span style={{ ...val, color: '#047857' }}>{money(entry.carry)}</span></div>}
        {onEmail && (
          <button onClick={onEmail} title={t('tx_vatmail_hint')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
            <i className="ti ti-mail-forward" style={{ fontSize: 15 }} />{t('tx_vatmail_btn')}
          </button>
        )}
      </>) : <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(hasChain ? 'tx_credit_starts_later' : 'tx_credit_nochain')}</span>}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
        {edit ? (<>
          <span style={lbl}>{t('tx_credit_open')}</span>
          <input value={amt} onChange={e => setAmt(e.target.value)} inputMode="decimal" placeholder="0.00" style={{ width: 90, padding: '4px 6px', border: '1px solid var(--border-strong)', borderRadius: 6, textAlign: 'right' }} />
          <span style={lbl}>{t('tx_credit_open_from')}</span>
          <input type="month" placeholder="YYYY-MM" value={ym} onChange={e => setYm(e.target.value)} style={{ padding: '3px 6px', border: '1px solid var(--border-strong)', borderRadius: 6 }} />
          <button onClick={save} disabled={busy} style={{ border: 'none', borderRadius: 6, padding: '5px 12px', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? <i className="ti ti-loader-2" style={{ animation: 'spin 0.7s linear infinite', display: 'inline-block' }} /> : t('save')}</button>
          <button onClick={() => setEdit(false)} style={{ border: '1px solid var(--border-strong)', borderRadius: 6, padding: '4px 10px', background: 'var(--bg-surface)', cursor: 'pointer' }}>{t('cancel')}</button>
        </>) : (
          <button onClick={start} title={t('tx_credit_open_hint')} style={{ border: '1px dashed var(--border-strong)', borderRadius: 6, padding: '4px 10px', background: 'var(--bg-surface)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <i className="ti ti-pencil" style={{ marginRight: 4 }} />{t('tx_credit_open')}: {money(opening.amount)}{opening.ym ? ` (${opening.ym})` : ''}
          </button>
        )}
      </div>
    </div>
    {/* Each additional ภ.พ.30 on its own line: paid in full on its own figures, never offset
        against the credit above, and any credit it produces is reference only — not carried. */}
    {adds.map(a => (
      <div key={a.filingNo} style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-strong)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, color: '#6D28D9' }}>
          <i className="ti ti-file-plus" />{t('tx_credit_add_no').replace('{n}', String(a.filingNo))}
        </div>
        <div style={box}><span style={lbl}>{t('tx_out_vat')}</span><span style={val}>{money(a.outVat)}</span></div>
        <div style={box}><span style={lbl}>{t('tx_in_vat')}</span><span style={val}>{money(a.inVat)}</span></div>
        <div style={box}><span style={{ ...lbl, color: '#B45309' }}>{t('tx_credit_payable')}</span><span style={{ ...val, color: '#B45309' }}>{money(a.payable)}</span></div>
        <div style={box}><span style={lbl}>{t('tx_credit_used')}</span><span style={{ ...val, color: 'var(--text-tertiary)' }}>0.00</span></div>
        {a.creditGenerated > 0 && (
          <div style={box}>
            <span style={{ ...lbl, color: '#047857' }}>{t('tx_credit_generated')}</span>
            <span style={{ ...val, color: '#047857' }}>{money(a.creditGenerated)}</span>
          </div>
        )}
        {a.creditGenerated > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic', maxWidth: 320 }}>{t('tx_credit_ref_only')}</span>
        )}
      </div>
    ))}
    </div>
  );
}

// ── Year summary (12 months) ─────────────────────────────────────────────────
function Summary({ monthly, hasVat, credit }: { monthly: any[]; hasVat: boolean; credit?: any[] }) {
  const cr = (m: number) => (credit && credit[m - 1]) || null;
  const { t } = useTranslation();
  const sumA0 = hasVat || monthly.some((m: any) => Number(m.output?.amount0) > 0.005 || Number(m.input?.amount0) > 0.005);
  const sth: React.CSSProperties = { ...th, textAlign: 'right' };
  const std: React.CSSProperties = { ...cell, textAlign: 'right' };
  const sum = monthly.reduce((a, m) => ({
    os: a.os + m.output.amount, ov: a.ov + m.output.vat, o0: a.o0 + (m.output.amount0 || 0), ot: a.ot + m.output.total,
    is: a.is + m.input.amount, iv: a.iv + m.input.vat, i0: a.i0 + (m.input.amount0 || 0), it: a.it + m.input.total,
    tax: a.tax + (cr(m.month) ? cr(m.month).payable : m.tax), wh: a.wh + m.output.withhold + m.input.withhold,
  }), { os: 0, ov: 0, o0: 0, ot: 0, is: 0, iv: 0, i0: 0, it: 0, tax: 0, wh: 0 });
  const yearEndCarry = (() => { for (let i = 11; i >= 0; i--) { const c = credit && credit[i]; if (c) return c.carry; } return 0; })();
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: hasVat ? 1000 : 900 }}>
        <thead><tr style={{ background: 'var(--c-gray-50)' }}>
          <th style={{ ...th, width: 70 }}>{t('tx_month')}</th>
          <th style={sth}>{t('tx_sales')}</th>{hasVat && <th style={sth}>{t('tx_vat')}</th>}{sumA0 && <th style={sth}>{t(hasVat ? 'tx_amount0' : 'tx_amount_nowht')}</th>}<th style={sth}>{t('tx_total')}</th>
          <th style={sth}>{t('tx_purchase')}</th>{hasVat && <th style={sth}>{t('tx_vat')}</th>}{sumA0 && <th style={sth}>{t(hasVat ? 'tx_amount0' : 'tx_amount_nowht')}</th>}<th style={sth}>{t('tx_total')}</th>
          {hasVat && <th style={sth}>{t('tx_credit_bf_col')}</th>}{hasVat && <th style={sth}>{t('tx_tax')}</th>}{hasVat && <th style={sth}>{t('tx_credit_cf_col')}</th>}<th style={{ ...sth, borderRight: 'none' }}>{t('tx_withhold')}</th>
        </tr></thead>
        <tbody>
          {monthly.map(m => (
            <tr key={m.month} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ ...cell, fontWeight: 600 }}>{MONTHS[m.month - 1]}</td>
              <td style={std}>{money(m.output.amount)}</td>{hasVat && <td style={std}>{money(m.output.vat)}</td>}{sumA0 && <td style={std}>{money(m.output.amount0 || 0)}</td>}<td style={std}>{money(m.output.total)}</td>
              <td style={std}>{money(m.input.amount)}</td>{hasVat && <td style={std}>{money(m.input.vat)}</td>}{sumA0 && <td style={std}>{money(m.input.amount0 || 0)}</td>}<td style={std}>{money(m.input.total)}</td>
              {hasVat && <td style={std}>{cr(m.month) ? money(cr(m.month).brought) : '—'}</td>}
              {hasVat && (() => { const c = cr(m.month); const pay = c ? c.payable : m.tax; return <td style={{ ...std, fontWeight: 600, color: pay > 0 ? '#D97706' : 'var(--text-tertiary)' }}>{money(pay)}</td>; })()}
              {hasVat && <td style={{ ...std, color: '#047857' }}>{cr(m.month) ? money(cr(m.month).carry) : '—'}</td>}
              <td style={{ ...std, borderRight: 'none' }}>{money(m.output.withhold + m.input.withhold)}</td>
            </tr>
          ))}
          <tr style={{ background: 'var(--c-gray-50)', fontWeight: 800 }}>
            <td style={cell}>{t('tx_total')}</td>
            <td style={std}>{money(sum.os)}</td>{hasVat && <td style={std}>{money(sum.ov)}</td>}{hasVat && <td style={std}>{money(sum.o0)}</td>}<td style={std}>{money(sum.ot)}</td>
            <td style={std}>{money(sum.is)}</td>{hasVat && <td style={std}>{money(sum.iv)}</td>}{hasVat && <td style={std}>{money(sum.i0)}</td>}<td style={std}>{money(sum.it)}</td>
            {hasVat && <td style={std} />}{hasVat && <td style={std}>{money(sum.tax)}</td>}{hasVat && <td style={{ ...std, color: '#047857' }}>{money(yearEndCarry)}</td>}<td style={{ ...std, borderRight: 'none' }}>{money(sum.wh)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── SSO (Social Security) — per-company monthly contribution table ────────────
// Red banner for a month the SYSTEM rebuilt rather than a person prepared. It appears on months that
// were marked filed but had never been saved — the figures were reconstructed from the roster, so
// they must be checked against the submitted paperwork before they are trusted. Clearing it changes
// no figures; it only records that a human has looked.
// (b13 S3) A read-only seat gets the words without the action: `onClear` undefined draws no button.
function BackfilledNote({ show, onClear }: { show: boolean; onClear?: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!show) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '9px 12px', marginBottom: 10 }}>
      <i className="ti ti-alert-triangle" style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, lineHeight: 1.5 }}>{t('pay_backfilled')}</div>
      {onClear && <button onClick={async () => { setBusy(true); try { await onClear(); } finally { setBusy(false); } }} disabled={busy}
        style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 7, border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', fontWeight: 700, fontSize: 11.5, cursor: busy ? 'default' : 'pointer' }}>
        <i className={`ti ti-${busy ? 'loader' : 'check'}`} style={{ marginRight: 4 }} />{t('pay_backfilled_ok')}
      </button>}
    </div>
  );
}

// ── b13 S3 (PAY-6): who changes these lines, and who reads their history ─────────────────────────────────────────
// A read-only seat (the auditor where the workspace is on — isPayLinesReadOnly, owner 2026-11-03; never a role literal) reads PND.1 and SSO and changes nothing: the
// server refuses its writes by method (middleware.ts), so the panels draw no write control for it — the lines are
// text, as the preview draws them; no Generate / Re-sync, no Add, no trash, no "reviewed"; Export and the PND.1 paper
// stay. A line's History is drawn only when the month payload says `history: true` (canSeePay — O2's white list), one
// button per saved line, opening a read-only card (PayLineHistory) — no request is sent that would be refused.
const histBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };
function HistoryBtn({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return <button type="button" onClick={onOpen} aria-label={t('plog_history')} title={t('plog_history')} style={histBtn} data-plog-open><i className="ti ti-history" aria-hidden /></button>;
}

// A month already submitted to the Revenue Department / SSO Office is a record of what was filed, so
// its lines are frozen server-side. The panel used to look fully editable anyway and only revealed the
// lock as an error alert AFTER someone had typed — an accountant could believe a correction had been
// saved when it had not. This states the lock up front and greys the controls out.
function FiledLock({ show, filingNo }: { show: boolean; filingNo: number }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: '#3F4A56', background: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 8, padding: '9px 12px', marginBottom: 10 }}>
      <i className="ti ti-lock" style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, lineHeight: 1.5 }}>{t('pay_filed_locked', { no: filingNo })}</div>
    </div>
  );
}

// Brief "saved" acknowledgement. Every cell writes to the server the moment it loses focus, with no
// Save button anywhere — without a confirmation there is nothing to distinguish "saved" from "typed
// but never committed".
function SavedFlash({ at }: { at: number }) {
  const { t } = useTranslation();
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!at) return;
    setOn(true);
    const h = setTimeout(() => setOn(false), 1800);
    return () => clearTimeout(h);
  }, [at]);
  return (
    <span aria-live="polite" style={{ fontSize: 11.5, fontWeight: 700, color: '#1D7C4D', opacity: on ? 1 : 0, transition: 'opacity .25s', whiteSpace: 'nowrap' }}>
      <i className="ti ti-check" style={{ marginRight: 3 }} />{t('pay_saved')}
    </span>
  );
}

// Enter commits the cell instead of doing nothing: blur fires the same save path the mouse does.
const commitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
};

// Inline-edit cell styles, shared by the SSO and PND.1 month snapshots — both are editable.
const numI: React.CSSProperties = { width: 96, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, textAlign: 'right', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const txtI: React.CSSProperties = { width: '100%', minWidth: 130, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const lockedI: React.CSSProperties = { background: '#F1F5F9', color: 'var(--text-tertiary)', cursor: 'not-allowed', borderStyle: 'dashed' };

// ── A month payroll has filed (batch 10 R7, design §3.5 "Tax 面板"; R7-14, R7-20) ─────────────────────
// Once a payroll run is POSTED for a month, that run writes the month's ภ.ง.ด.1 and สปส. rows itself
// (services/payrollSnapshot.ts writePayrollMonth): `source = 'payroll'`, the run's own taxable pay, tax and
// SSO base frozen as overrides. Those rows are the run's, not this screen's — the server answers 409 to an
// edit, a delete or a Re-sync of them (routes/pnd1.ts, routes/sso.ts), and a refused write pages the admins.
// So the screen states it before anyone types: such a row is TEXT in the locked look, never an input; its
// trash cell is the run's number; Re-sync is off while a run owns the month, and says why. Fill rows
// (people a run did not cover, written from the roster) and hand-added rows stay editable, and Add stays.
//
// ⛔ THE CHIP IS A NEW-TAB LINK, not ctx.setNav (R7-14). This panel has four hosts — the Tax module, the
// workspace's Tax pane, the client dashboard and the focus dashboard — and three of them are not the
// accounting workspace at all, so there is no shell to move. And it is a link only for a seat that can open
// a payroll run: one holding accounting_books that is not read-only (a client's books login and the
// auditor read; neither opens Payroll › Payroll List). Anybody else gets the same words as plain text —
// a link that lands on "needs permission" is a dead link with extra steps.
//
// ⛔ "—" FOR ALLOWANCES AND NET on a payroll row (R7-20). The panel's breakdown is today's formula over the
// row's wage; a run's tax is worked out year-to-date by its own method, so printing the formula's net beside
// the run's tax would show two methods' answers as one. The hover names the run lines behind the row
// instead — the server's own byRow figures — and the full working stays in the run, where O2 decides who
// reads it.
type PayrollRunRef = { id: string; docNo: string; status: string };
type PayrollRowRun = { runId: string; docNo: string; lineId: string; taxableSat: number; whtSat: number; ssoBaseSat: number; ssoEeSat: number };
type PayrollMonth = { runs: PayrollRunRef[]; preparing: PayrollRunRef[]; uncovered: number };

const isPayrollRow = (l: any): boolean => String(l?.source || '') === 'payroll';
/** The runs a payroll row was written by, one per run, from the server's byRow list — never parsed out of
 *  `payrollRef`, which is a display string. */
const rowRuns = (l: any): PayrollRowRun[] => {
  const seen = new Set<string>();
  return ((l?.payroll?.runs || []) as PayrollRowRun[]).filter(r => !!r?.runId && !seen.has(r.runId) && !!seen.add(r.runId));
};
const docNosOf = (xs: { docNo?: string }[] | undefined): string => Array.from(new Set((xs || []).map(x => String(x?.docNo || '')).filter(Boolean))).join(', ');
const payrollOf = (data: any): PayrollMonth => ({
  runs: data?.payroll?.runs || [], preparing: data?.payroll?.preparing || [], uncovered: Number(data?.payroll?.uncovered) || 0,
});

/** Where a run opens: the accounting workspace's Payroll › Payroll List, on that run (R7 brief §4.6). */
export const payrollRunHref = (clientId: string, runId: string): string =>
  `/m/accounting_books?${new URLSearchParams({ client: clientId, menu: 'payroll', sub: 'payroll_list', run: runId }).toString()}`;

const chipS: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
  whiteSpace: 'nowrap', background: 'rgba(14,124,102,0.08)', color: '#0E7C66', border: '1px solid rgba(14,124,102,0.28)', textDecoration: 'none',
};

/** "Payroll PR-…" in a payroll row's trash cell — a new-tab link for a seat that can open the run, else text. */
function PayrollChip({ clientId, run }: { clientId: string; run: { runId: string; docNo: string } }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const label = t('pay_payroll_line', { docNo: run.docNo });
  const canOpen = !!run.runId && (me?.modules || []).includes('accounting_books') && !isReadOnlyRole(me?.role);
  if (!canOpen) return <span style={{ ...chipS, cursor: 'default' }} data-payroll-chip={run.runId}><i className="ti ti-users" aria-hidden />{label}</span>;
  return (
    <a href={payrollRunHref(clientId, run.runId)} target="_blank" rel="noopener noreferrer" style={chipS} data-payroll-chip={run.runId}
      title={t('pay_payroll_open', { docNo: run.docNo })}>
      <i className="ti ti-users" aria-hidden />{label}
    </a>
  );
}
/** The trash cell of a payroll row: one chip per run that wrote it (a monthly run and an off-cycle can share one).
 *  Should a row ever arrive without its byRow list, its `payrollRef` is shown as it is — text, never a link
 *  guessed from a number. */
const PayrollChips = ({ clientId, l }: { clientId: string; l: any }) => {
  const runs = rowRuns(l);
  const shown = runs.length ? runs : String(l?.payrollRef || '').split(',').map(s => s.trim()).filter(Boolean).map(docNo => ({ runId: '', docNo }));
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>{shown.map(r => <PayrollChip key={r.runId || r.docNo} clientId={clientId} run={r} />)}</div>;
};

/** A payroll row's value: read-only text in the locked look — never an input the server would refuse. */
const PayrollText = ({ base, children }: { base: React.CSSProperties; children: React.ReactNode }) => (
  <span style={{ ...base, ...lockedI, display: 'inline-block', cursor: 'default' }} data-payroll-text>{children}</span>
);

/** "This month is filed from payroll …" and "… is being prepared". The server's own lists, in its order. */
function PayrollMonthBanners({ p }: { p: PayrollMonth }) {
  const { t } = useTranslation();
  const owned = docNosOf(p.runs), prep = docNosOf(p.preparing);
  const box: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, borderRadius: 8, padding: '9px 12px', marginBottom: 10, lineHeight: 1.5 };
  return (
    <>
      {!!p.runs.length && (
        <div style={{ ...box, color: '#0B5D4D', background: 'rgba(14,124,102,0.07)', border: '1px solid rgba(14,124,102,0.28)' }} data-payroll-owned>
          <i className="ti ti-users" style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1 }}>
            <div>{t('pay_payroll_owned', { docNos: owned, n: p.uncovered })}</div>
            <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{t('pay_payroll_resync_off', { docNos: owned })}</div>
          </div>
        </div>
      )}
      {!!p.preparing.length && (
        <div style={{ ...box, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A' }} data-payroll-preparing>
          <i className="ti ti-hourglass" style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1 }}>{t('pay_payroll_preparing', { docNos: prep })}</div>
        </div>
      )}
    </>
  );
}

// ── 客户支付给 SLF 的时间 (owner 2026-09-09) ─────────────────────────────────────────────────────
// One line at the top of a filing screen answering "has the client actually given us the money for
// this, and when". Deliberately silent when the answer is unknown: a tax the client's payment never
// covered shows nothing at all rather than borrowing the date from the tax next to it. And a date
// only appears once the money is ALL in — a part payment says so, and says what is still short,
// because a date on a half-paid month reads as settled.
//
// This is NOT the remittance date (the green Paid stamp on a WHT line, and the Paid tick on the
// bank Collection cell): that one says the tax reached the Revenue Department. Both are shown.
function ClientPaidStamp({ clientId, year, month, taxKey }: { clientId: string; year: number; month: number; taxKey: string }) {
  const { t } = useTranslation();
  const [p, setP] = useState<any>(null);
  useEffect(() => {
    let alive = true;
    if (!clientId || !year) return;
    api.taxClientPaid(clientId, year, month || 0)
      .then((d: any) => { if (alive) setP(d?.[taxKey] || null); })
      .catch(() => { if (alive) setP(null); });
    return () => { alive = false; };
  }, [clientId, year, month, taxKey]);
  if (!p || !p.source) return null;
  const paid = !!p.complete && !!p.paidOn;
  return (
    <div title={p.detail || ''} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '5px 11px',
      borderRadius: 999, marginBottom: 10,
      background: paid ? 'rgba(21,128,61,0.09)' : '#FEF3C7',
      color: paid ? '#15803D' : '#92400E',
      border: `1px solid ${paid ? 'rgba(21,128,61,0.30)' : '#FDE68A'}`,
    }}>
      <i className={paid ? 'ti ti-cash-banknote' : 'ti ti-alert-triangle'} style={{ fontSize: 13 }} aria-hidden />
      {paid
        ? `${t('tx_client_paid')} · ${String(p.paidOn).split('-').reverse().join('/')}`
        : `${t('tx_client_part_paid')} · ${t('tx_client_outstanding', { amt: money(p.outstanding) })}`}
    </div>
  );
}

export function SsoPanel({ clientId, year, month: fixedMonth }: { clientId: string; year: number; month?: number }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(fixedMonth ?? new Date().getMonth() + 1);
  useEffect(() => { if (fixedMonth != null) setMonth(fixedMonth); }, [fixedMonth]);   // follow the selected month tab
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [savedAt, setSavedAt] = useState(0);
  const ro = useStore(s => isPayLinesReadOnly(s.me));   // b13 S3: the auditor reads where the workspace is on (see HistoryBtn's head)
  const [hist, setHist] = useState('');
  const load = async () => { setLoading(true); try { const d: any = await api.ssoGet(clientId, year, month); setData(d); } catch { setData(null); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [clientId, year, month]); // eslint-disable-line

  const snap = !!data?.snapshot;
  const filedMonth = !!data?.filed;
  const nextFno = Number(data?.openFilingNo || 0);
  const lines: any[] = data?.lines || [];
  const tot = data?.totals || { wage: 0, employee: 0, employer: 0, total: 0, count: 0 };
  const cfg = data?.config || { rate: 5, cap: 875, floor: 1650 };
  // A posted payroll run owns this month (batch 10 R7): its rows are its own, and Re-sync would rebuild over it.
  const pay = payrollOf(data);
  const owned = pay.runs.length > 0;
  const resyncOff = filedMonth || owned;
  const history = !!data?.history;   // b13 S3: the server's canSeePay, never inferred here

  const init = async () => { if (owned) return; setBusy(true); try { const d: any = await api.ssoInit(clientId, year, month); setData(d); } finally { setBusy(false); } };
  const editField = async (id: string, patch: any) => { const ok = await api.ssoUpdateLine(id, patch).then(() => true).catch((e: any) => { alert(e.message); return false; }); if (ok) setSavedAt(Date.now()); load(); };
  const addRow = async () => { setBusy(true); try { await api.ssoAddLine(clientId, { year, month, name: '', idNumber: '', wage: 0 }); await load(); } finally { setBusy(false); } };
  const delRow = async (id: string) => { await api.ssoDeleteLine(id).catch(() => {}); load(); };

  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see th at the top of the file). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see th there too).
  const h: React.CSSProperties = { padding: '10px 10px 5px', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const d: React.CSSProperties = { padding: '6px 10px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' };
  const rt: React.CSSProperties = { textAlign: 'right' };

  const btnP: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: 'none', background: '#1D7C4D', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };
  const btnG: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {fixedMonth != null
          ? <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t('tx_sso')} · {MONTHS[month - 1]} {year}</span>
          : <>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('tx_month')}</label>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m} {year}</option>)}
              </select>
            </>}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{cfg.rate}% · {t('sso_cap')} {moneyTight(cfg.cap)}</span>
        <div style={{ flex: 1 }} />
        <SavedFlash at={savedAt} />
        {!ro && <button onClick={init} disabled={busy || resyncOff} data-resync
          title={owned ? t('pay_payroll_resync_off', { docNos: docNosOf(pay.runs) }) : filedMonth ? t('pay_filed_short') : ''}
          style={{ ...(snap ? btnG : btnP), ...(resyncOff ? { opacity: .45, cursor: 'not-allowed' } : {}) }}><i className={`ti ti-${snap ? 'refresh' : 'device-floppy'}`} style={{ marginRight: 5 }} />{snap ? t('sso_resync') : t('sso_generate')}</button>}
        <DlBtn onRun={() => api.ssoExport(clientId, year, month)} disabled={!lines.length} style={btnG}><i className="ti ti-file-spreadsheet" style={{ marginRight: 5 }} />{t('tx_export')}</DlBtn>
      </div>

      <FiledLock show={filedMonth} filingNo={nextFno} />
      <PayrollMonthBanners p={pay} />
      <BackfilledNote show={!!data?.backfilled} onClear={ro ? undefined : async () => { await api.ssoMarkReviewed(clientId, year, month).catch(() => {}); load(); }} />

      <div style={{ fontSize: 12, color: snap ? 'var(--text-tertiary)' : '#B45309', background: snap ? 'transparent' : '#FFFBEB', border: snap ? 'none' : '1px solid #FDE68A', borderRadius: 8, padding: snap ? 0 : '8px 12px', marginBottom: 12 }}>
        <i className={`ti ti-${snap ? 'info-circle' : 'bulb'}`} style={{ marginRight: 6 }} />{/* (b13 review) A read-only seat is told the month is read only for it, not invited to edit or Generate. */}
        {ro ? t(snap ? 'plog_ro_snap' : 'plog_ro_preview') : snap ? t('sso_snapshot_hint') : t('sso_preview_hint')}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
        : lines.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('sso_no_emps')}</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={{ ...h, width: 34 }}>#</th>
              <th style={h}>{t('sso_emp_id')}</th>
              <th style={h}>{t('sso_name')}</th>
              <th style={{ ...h, ...rt }}>{t('sso_wage')}</th>
              <th style={{ ...h, ...rt }}>{t('sso_employee')}</th>
              <th style={{ ...h, ...rt }}>{t('sso_employer')}</th>
              <th style={{ ...h, ...rt }}>{t('sso_total')}</th>
              {snap && <th style={{ ...h, width: lines.some(isPayrollRow) || history ? undefined : 34 }} />}
            </tr></thead>
            <tbody>
              {lines.map((l: any, i: number) => {
                // A payroll row is the run's: text, and its run's number where the trash would be (see the head).
                const pr = isPayrollRow(l);
                // b13 S3: a read-only seat reads the saved month as the preview draws it — text, no input.
                const edit = snap && !ro;
                return (
                <tr key={l.id || i} data-sso-row={l.source || 'roster'}>
                  <td style={{ ...d, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                  <td style={d}>{pr ? <PayrollText base={{ ...txtI, minWidth: 120 }}>{l.id_number || '—'}</PayrollText> : edit ? <input defaultValue={l.id_number} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => e.target.value !== l.id_number && editField(l.id, { idNumber: e.target.value })} style={{ ...txtI, minWidth: 120, ...(l.locked ? lockedI : {}) }} /> : (l.id_number || '—')}</td>
                  <td style={d}>{pr ? <PayrollText base={txtI}>{l.name || '—'}</PayrollText> : edit ? <input defaultValue={l.name} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => e.target.value !== l.name && editField(l.id, { name: e.target.value })} style={{ ...txtI, ...(l.locked ? lockedI : {}) }} /> : l.name}</td>
                  <td style={{ ...d, ...rt }}>{pr ? <PayrollText base={numI}>{money(l.wage)}</PayrollText> : edit ? <input defaultValue={l.wage} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => Number(e.target.value) !== l.wage && editField(l.id, { wage: Number(e.target.value) || 0 })} style={{ ...numI, ...(l.locked ? lockedI : {}) }} inputMode="decimal" /> : money(l.wage)}</td>
                  <td style={{ ...d, ...rt, fontWeight: 600 }}>{money(l.employee)}</td>
                  <td style={{ ...d, ...rt }}>{money(l.employer)}</td>
                  <td style={{ ...d, ...rt, fontWeight: 700 }}>{money(l.total)}</td>
                  {snap && <td style={{ ...d, textAlign: 'center', whiteSpace: 'nowrap' }}>{pr
                    ? <PayrollChips clientId={clientId} l={l} />
                    : l.locked
                    ? <i className="ti ti-lock" title={t('pay_filed_short')} style={{ color: 'var(--text-tertiary)' }} />
                    : ro ? null
                    : <button onClick={() => delRow(l.id)} title={t('delete')} style={{ border: 'none', background: 'transparent', color: '#C2410C', cursor: 'pointer' }}><i className="ti ti-trash" /></button>}
                    {history && !!l.id && <HistoryBtn onOpen={() => setHist(String(l.id))} />}</td>}
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg-surface)', fontWeight: 700 }}>
                <td style={d} />
                <td style={d} colSpan={2}>{t('sso_company_total')} · {tot.count} {t('sso_emps')}</td>
                <td style={{ ...d, ...rt }}>{money(tot.wage)}</td>
                <td style={{ ...d, ...rt }}>{money(tot.employee)}</td>
                <td style={{ ...d, ...rt }}>{money(tot.employer)}</td>
                <td style={{ ...d, ...rt }}>{money(tot.total)}</td>
                {snap && <td style={d} />}
              </tr>
            </tfoot>
          </table>
        )}

      {snap && !ro && <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={addRow} disabled={busy} style={btnG}><i className="ti ti-plus" style={{ marginRight: 5 }} />{t('sso_add_emp')}</button>
        {filedMonth && <span style={{ fontSize: 11.5, color: '#B45309' }}><i className="ti ti-info-circle" style={{ marginRight: 4 }} />{t('pay_add_goes_to', { no: nextFno })}</span>}
      </div>}
      {hist && <PayLineHistory kind="sso" lineId={hist} onClose={() => setHist('')} />}
    </div>
  );
}

// ── PND.1 — per-company monthly payroll income-tax withholding (per month, like SSO) ──────────
export function Pnd1Panel({ clientId, year, month: fixedMonth }: { clientId: string; year: number; month?: number }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(fixedMonth ?? new Date().getMonth() + 1);
  useEffect(() => { if (fixedMonth != null) setMonth(fixedMonth); }, [fixedMonth]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hoverNet, setHoverNet] = useState<{ x: number; y: number; l: any } | null>(null);

  const [savedAt, setSavedAt] = useState(0);
  const ro = useStore(s => isPayLinesReadOnly(s.me));   // b13 S3: the auditor reads where the workspace is on (see HistoryBtn's head)
  const [hist, setHist] = useState('');
  const load = async () => { setLoading(true); try { const d: any = await api.pnd1Get(clientId, year, month); setData(d); } catch { setData(null); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [clientId, year, month]); // eslint-disable-line

  const snap = !!data?.snapshot;
  const filedMonth = !!data?.filed;
  const nextFno = Number(data?.openFilingNo || 0);
  const lines: any[] = data?.lines || [];
  const tot = data?.totals || { wage: 0, net: 0, monthly: 0, count: 0 };
  const cfg = data?.config || { expenseRate: 50, expenseCap: 100000, ssoCap: 9000, deductions: [] };
  const cfgAllow = (cfg.deductions || []).reduce((s: number, d: any) => s + (Number(d.amount) || 0), 0);
  // A posted payroll run owns this month (batch 10 R7): its rows are its own, and Re-sync would rebuild over it.
  const pay = payrollOf(data);
  const owned = pay.runs.length > 0;
  const resyncOff = filedMonth || owned;
  // The formula's net is not the run's (R7-20): a month holding a payroll row has no one net total to print.
  const anyPayroll = lines.some(isPayrollRow);
  const history = !!data?.history;   // b13 S3: the server's canSeePay, never inferred here

  // A saved PND.1 month is EDITABLE, exactly as SSO is (owner 2026-08-11): a filed return often has
  // to carry a correction the roster does not know about. The backend has always supported it
  // (POST/PATCH/DELETE /pnd1/lines, with the filed-month and per-submission locks); only this UI
  // withheld it. "Re-sync from roster" still rebuilds the whole month from TODAY's salaries — which
  // is exactly why it now asks first.
  const init = async () => {
    if (owned) return;
    if (snap && !window.confirm(t('pnd1_resync_warn'))) return;
    setBusy(true); try { const d: any = await api.pnd1Init(clientId, year, month); setData(d); } finally { setBusy(false); }
  };
  const editField = async (id: string, patch: any) => { const ok = await api.pnd1UpdateLine(id, patch).then(() => true).catch((e: any) => { alert(e.message); return false; }); if (ok) setSavedAt(Date.now()); load(); };
  const addRow = async () => { setBusy(true); try { await api.pnd1AddLine(clientId, { year, month, name: '', idNumber: '', wage: 0 }); await load(); } finally { setBusy(false); } };
  const delRow = async (id: string) => { await api.pnd1DeleteLine(id).catch((e: any) => alert(e.message)); load(); };

  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see th at the top of the file). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see th there too).
  const h: React.CSSProperties = { padding: '10px 10px 5px', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const d: React.CSSProperties = { padding: '6px 10px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' };
  const rt: React.CSSProperties = { textAlign: 'right' };
  const btnP: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: 'none', background: '#1D7C4D', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };
  const btnG: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {fixedMonth != null
          ? <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t('tx_pnd1')} · {MONTHS[month - 1]} {year}</span>
          : <>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('tx_month')}</label>
              <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m} {year}</option>)}
              </select>
            </>}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pnd1_deduct_hint', { exp: cfg.expenseRate, cap: money(cfg.expenseCap), per: money(cfgAllow) })}</span>
        <div style={{ flex: 1 }} />
        <SavedFlash at={savedAt} />
        {!ro && <button onClick={init} disabled={busy || resyncOff} data-resync
          title={owned ? t('pay_payroll_resync_off', { docNos: docNosOf(pay.runs) }) : filedMonth ? t('pay_filed_short') : ''}
          style={{ ...(snap ? btnG : btnP), ...(resyncOff ? { opacity: .45, cursor: 'not-allowed' } : {}) }}><i className={`ti ti-${snap ? 'refresh' : 'device-floppy'}`} style={{ marginRight: 5 }} />{snap ? t('sso_resync') : t('sso_generate')}</button>}
        <DlBtn onRun={() => api.pnd1Export(clientId, year, month)} disabled={!lines.length} style={btnG}><i className="ti ti-file-spreadsheet" style={{ marginRight: 5 }} />{t('tx_export')}</DlBtn>
        {/* Paper ภ.ง.ด.1 on the official RD form — Normal filing (owner 2026-09-10). */}
        <DlBtn onRun={() => api.pnd1PaperPdf(clientId, year, month, 0, true, `pnd1-${year}-${String(month).padStart(2, '0')}.pdf`)}
          disabled={!lines.length} title={t('tx_pnd_paper_hint')}
          style={{ ...btnG, border: '1px solid #0E7C66', color: '#0E7C66', background: 'transparent' }}>
          <i className="ti ti-file-text" style={{ marginRight: 5 }} />{t('tx_pnd_paper', { no: '1' })}</DlBtn>
      </div>

      <FiledLock show={filedMonth} filingNo={nextFno} />
      <PayrollMonthBanners p={pay} />
      <BackfilledNote show={!!data?.backfilled} onClear={ro ? undefined : async () => { await api.pnd1MarkReviewed(clientId, year, month).catch(() => {}); load(); }} />

      <div style={{ fontSize: 12, color: snap ? 'var(--text-tertiary)' : '#B45309', background: snap ? 'transparent' : '#FFFBEB', border: snap ? 'none' : '1px solid #FDE68A', borderRadius: 8, padding: snap ? 0 : '8px 12px', marginBottom: 12 }}>
        <i className={`ti ti-${snap ? 'info-circle' : 'bulb'}`} style={{ marginRight: 6 }} />{/* (b13 review) A read-only seat is told the month is read only for it, not invited to edit or Generate. */}
        {ro ? t(snap ? 'plog_ro_snap' : 'plog_ro_preview') : snap ? t('pnd1_snap_hint') : t('pnd1_preview_hint')}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
        : lines.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('sso_no_emps')}</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead><tr>
              <th style={{ ...h, width: 34 }}>#</th>
              <th style={h}>{t('sso_emp_id')}</th>
              <th style={h}>{t('sso_name')}</th>
              <th style={{ ...h, ...rt }}>{t('pnd1_salary')}</th>
              <th style={{ ...h, ...rt }}>{t('pnd1_allow')}</th>
              <th style={{ ...h, ...rt }}>{t('pnd1_net')}</th>
              <th style={{ ...h, ...rt }}>{t('pnd1_wht')}</th>
              {snap && <th style={{ ...h, width: anyPayroll || history ? undefined : 34 }} />}
            </tr></thead>
            <tbody>
              {lines.map((l: any, i: number) => {
                // A payroll row is the run's: text, "—" where the formula's working would be, and its run's
                // number where the trash would be (see the head).
                const pr = isPayrollRow(l);
                // b13 S3: a read-only seat reads the saved month as the preview draws it — text, no input.
                const edit = snap && !ro;
                const hover = {
                  onMouseEnter: (e: React.MouseEvent) => setHoverNet({ x: e.clientX, y: e.clientY, l }),
                  onMouseMove: (e: React.MouseEvent) => setHoverNet(h => (h ? { ...h, x: e.clientX, y: e.clientY } : h)),
                  onMouseLeave: () => setHoverNet(null),
                };
                return (
                <tr key={l.id || i} data-pnd1-row={l.source || 'roster'}>
                  <td style={{ ...d, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                  <td style={d}>{pr ? <PayrollText base={{ ...txtI, minWidth: 120 }}>{l.id_number || '—'}</PayrollText> : edit ? <input defaultValue={l.id_number} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => e.target.value !== l.id_number && editField(l.id, { idNumber: e.target.value })} style={{ ...txtI, minWidth: 120, ...(l.locked ? lockedI : {}) }} /> : (l.id_number || '—')}</td>
                  <td style={d}>{pr ? <PayrollText base={txtI}>{l.name || '—'}</PayrollText> : edit ? <input defaultValue={l.name} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => e.target.value !== l.name && editField(l.id, { name: e.target.value })} style={{ ...txtI, ...(l.locked ? lockedI : {}) }} /> : (l.name || '—')}</td>
                  <td style={{ ...d, ...rt }}>{pr ? <PayrollText base={numI}>{money(l.wage)}</PayrollText> : edit ? <input defaultValue={l.wage} disabled={l.locked} onKeyDown={commitOnEnter} onBlur={e => Number(e.target.value) !== l.wage && editField(l.id, { wage: Number(e.target.value) || 0 })} style={{ ...numI, ...(l.locked ? lockedI : {}) }} inputMode="decimal" /> : money(l.wage)}</td>
                  {pr
                    ? <td style={{ ...d, ...rt, color: 'var(--text-tertiary)', cursor: 'help' }} data-payroll-dash="allow" {...hover}>—</td>
                    : <td style={{ ...d, ...rt, whiteSpace: 'nowrap' }}>{money(l.allowTotal || 0)}</td>}
                  <td style={{ ...d, ...rt, color: 'var(--text-secondary)', cursor: 'help' }} {...hover} {...(pr ? { 'data-payroll-dash': 'net' } : {})}>
                    {pr ? <span style={{ color: 'var(--text-tertiary)' }}>—</span> : <span style={{ borderBottom: '1px dotted var(--text-tertiary)' }}>{money(l.net)}</span>}
                  </td>
                  <td style={{ ...d, ...rt, fontWeight: 700 }}>{money(l.monthly)}</td>
                  {snap && <td style={{ ...d, textAlign: 'center', whiteSpace: 'nowrap' }}>{pr
                    ? <PayrollChips clientId={clientId} l={l} />
                    : l.locked
                    ? <i className="ti ti-lock" title={t('pay_filed_short')} style={{ color: 'var(--text-tertiary)' }} />
                    : ro ? null
                    : <button onClick={() => delRow(l.id)} title={t('delete')} style={{ border: 'none', background: 'transparent', color: '#C2410C', cursor: 'pointer' }}><i className="ti ti-trash" /></button>}
                    {history && !!l.id && <HistoryBtn onOpen={() => setHist(String(l.id))} />}</td>}
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg-surface)', fontWeight: 700 }}>
                <td style={d} />
                <td style={d} colSpan={2}>{t('sso_company_total')} · {tot.count} {t('sso_emps')}</td>
                <td style={{ ...d, ...rt }}>{money(tot.wage)}</td>
                <td style={d} />
                <td style={{ ...d, ...rt }}>{anyPayroll ? '—' : money(tot.net)}</td>
                <td style={{ ...d, ...rt }}>{money(tot.monthly)}</td>
                {snap && <td style={d} />}
              </tr>
            </tfoot>
          </table>
        )}

      {snap && !ro && <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={addRow} disabled={busy} style={btnG}><i className="ti ti-plus" style={{ marginRight: 5 }} />{t('sso_add_emp')}</button>
        {filedMonth && <span style={{ fontSize: 11.5, color: '#B45309' }}><i className="ti ti-info-circle" style={{ marginRight: 4 }} />{t('pay_add_goes_to', { no: nextFno })}</span>}
      </div>}
      {hist && <PayLineHistory kind="pnd1" lineId={hist} onClose={() => setHist('')} />}

      {hoverNet && (() => {
        const l = hoverNet.l;
        const rowS: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 18, fontSize: 12, padding: '2px 0' };
        const ded: React.CSSProperties = { ...rowS, color: '#B45309' };
        const boxS: React.CSSProperties = { position: 'fixed', left: Math.min(hoverNet.x + 16, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 270), top: hoverNet.y + 16, zIndex: 4000, width: 252, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: '0 8px 30px #00000026', padding: '10px 12px', pointerEvents: 'none' };
        if (isPayrollRow(l)) {
          // The run lines behind a payroll row, as the server lists them — never the formula's working (R7-20).
          return (
            <div style={boxS} data-payroll-hover>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('pay_payroll_hover_title')}</div>
              {rowRuns(l).map(r => (
                <div key={r.runId} style={{ ...rowS, display: 'block' }} data-payroll-hover-run={r.runId}>
                  {t('pay_payroll_hover_row', { docNo: r.docNo, taxable: moneyTight((Number(r.taxableSat) || 0) / 100), wht: moneyTight((Number(r.whtSat) || 0) / 100) })}
                </div>
              ))}
            </div>
          );
        }
        return (
          <div style={boxS}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('pnd1_bd_title')}</div>
            <div style={{ ...rowS, fontWeight: 700 }}><span>{t('pnd1_bd_annual')}</span><span>{moneyTight(l.annual)}</span></div>
            <div style={ded}><span>− {t('pnd1_bd_expense')}</span><span>{moneyTight(l.expense)}</span></div>
            <div style={ded}><span>− {t('pnd1_bd_global')}</span><span>{moneyTight(l.globalAllow)}</span></div>
            {l.allowTotal > 0 && <div style={ded}><span>− {t('pnd1_bd_emp')}</span><span>{moneyTight(l.allowTotal)}</span></div>}
            {l.sso > 0 && <div style={ded}><span>− {t('pnd1_bd_sso')}</span><span>{moneyTight(l.sso)}</span></div>}
            <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0 4px' }} />
            <div style={{ ...rowS, fontWeight: 800, color: 'var(--text-primary)' }}><span>{t('pnd1_bd_net')}</span><span>{moneyTight(l.net)}</span></div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Company WHT — PND.3 (individuals) / PND.53 (companies), per month ─────────────────────────
// Exported for scripts/testExpensePaymentScreen.ts (batch 11 R5), which renders it by itself — the one Tax panel a supplier payment writes into.
export function WhtPanel({ clientId, year, month, payeeType }: { clientId: string; year: number; month: number; payeeType?: 'individual' | 'company' }) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => { setLoading(true); try { setData(await api.whtGet(clientId, year, month)); } catch { setData(null); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [clientId, year, month]); // eslint-disable-line
  // PND.3 = withholding on INDIVIDUALS, PND.53 = on COMPANIES. When a payeeType is given (the PND.3 /
  // PND.53 dropdown options), show only that side; otherwise show all WHT lines together.
  const pndCode = payeeType === 'individual' ? '3' : payeeType === 'company' ? '53' : '';
  const whtKind = payeeType === 'individual' ? 'pnd3' : 'pnd53';
  const [whtFiling, setWhtFiling] = useState<'all' | number>('all');
  useEffect(() => { setWhtFiling('all'); }, [clientId, year, month, payeeType]);
  const lines: any[] = (data?.lines || []).filter((l: any) => (!payeeType || l.payee_type === payeeType)
    && (whtFiling === 'all' || Number(l.filing_no || 0) === whtFiling));
  const types: any[] = data?.incomeTypes || [];
  // The footer has to state what the table above SHOWS. It used to read data.totals, which the
  // server computes over the WHOLE MONTH across every submission — so with Normal/Additional
  // chips both tabs printed the same figure, silently added together (owner 2026-09-18: Normal
  // 600.00 and Additional 559.20 each displayed as 1,159.20). Summing the filtered rows makes
  // the payee-type filter agree too, and 'All' still shows the month.
  const sumPnd = (p: string) => lines.filter((l: any) => String(l.pnd) === p)
    .reduce((a: any, l: any) => ({ gross: a.gross + (Number(l.gross) || 0), wht: a.wht + (Number(l.wht) || 0), count: a.count + 1 }),
      { gross: 0, wht: 0, count: 0 });
  const tot = { pnd3: sumPnd('3'), pnd53: sumPnd('53') };
  const editField = async (id: string, patch: any) => { await api.whtUpdateLine(id, patch).catch(() => {}); load(); };
  const addRow = async () => { setBusy(true); try { await api.whtAddLine(clientId, { year, month, payeeType: payeeType || 'company', payeeName: '', incomeType: types[0]?.label || '', rate: types[0]?.rate || 0, gross: 0, filingNo: whtFiling === 'all' ? undefined : whtFiling }); await load(); } finally { setBusy(false); } };
  const delRow = async (id: string) => { try { await api.whtDeleteLine(id); } catch (e: any) { alert(e?.message || 'Delete failed'); } load(); };
  // Editing the money fields of an already-collected (bank-matched) line desyncs it from the bank
  // record — warn first; cancelling reloads so the input snaps back.
  const editMoney = async (l: any, patch: any) => {
    if (Number(l.paid_at) > 0 && !window.confirm(t('tx_wht_paid_edit_warn'))) { load(); return; }
    await editField(l.id, patch);
  };

  // Row selection — drives Form 50 Tawi (nothing ticked = every certifiable line on this page).
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  useEffect(() => { setSelected(new Set()); }, [clientId, year, month, payeeType]);
  const toggleSel = (id: string) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selHere = lines.filter((l: any) => selected.has(l.id));
  const allChecked = lines.length > 0 && selHere.length === lines.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(lines.map((l: any) => l.id)));

  // ── Form 50 Tawi (WHT certificate) — one THAI certificate page per withholding line. Issued from
  //    THESE records, which is why the button sits on PND.3 / PND.53 rather than on the VAT filing.
  const [tawiBusy, setTawiBusy] = useState(false);
  // Stamp & sign toggle (owner 2026-09-10): shown only when the server says the client has
  // authorized assets; default ON — un-tick for a batch the client will stamp in ink.
  const [tawiStamp, setTawiStamp] = useState(true);
  const [xlsBusy, setXlsBusy] = useState(false);
  const certifiable = lines.filter((l: any) => Number(l.wht) > 0);
  const downloadTawi50 = async () => {
    if (tawiBusy) return;
    const pool = (selHere.length ? selHere : lines).filter((l: any) => Number(l.wht) > 0);
    if (!pool.length) { alert(t('tx_tawi50_none')); return; }
    // A certificate names the payee — a blank name would print a certificate nobody can use.
    const noName = pool.filter((l: any) => !String(l.payee_name || '').trim()).length;
    if (noName && !window.confirm(t('tx_tawi50_noname', { n: noName }))) return;
    setTawiBusy(true);
    try {
      const nm = String(data?.client?.company_name || data?.client?.client_name || 'client').replace(/[^\w-]+/g, '_');
      await api.tawi50Pdf(clientId, pool.map((l: any) => l.id), `50tawi-${nm}-${year}-${String(month).padStart(2, '0')}.pdf`, !data?.stampReady || tawiStamp);
    } catch (e: any) { alert(e.message); }
    finally { setTawiBusy(false); }
  };
  const downloadXls = async () => {
    if (xlsBusy) return;
    setXlsBusy(true);
    try { await api.whtExport(clientId, year, month, whtFiling === 'all' ? undefined : whtFiling); } catch (e: any) { alert(e.message); }
    finally { setXlsBusy(false); }
  };
  // Paper ภ.ง.ด.3/53 on the official RD form (owner 2026-09-10). The return covers ONE filing —
  // the current Submission tab; on "All" it prints the Normal filing.
  const [pndBusy, setPndBusy] = useState(false);
  const downloadPndPaper = async () => {
    if (pndBusy || !payeeType) return;
    setPndBusy(true);
    try {
      const nm = String(data?.client?.company_name || data?.client?.client_name || 'client').replace(/[^\w-]+/g, '_');
      const filing = whtFiling === 'all' ? 0 : whtFiling;
      await api.pndPaperPdf(clientId, whtKind as any, year, month, filing,
        !data?.stampReady || tawiStamp,
        `pnd${pndCode}-${nm}-${year}-${String(month).padStart(2, '0')}${filing ? `-add${filing}` : ''}.pdf`);
    } catch (e: any) { alert(e.message); }
    finally { setPndBusy(false); }
  };

  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see th at the top of the file). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see th there too).
  const h: React.CSSProperties = { padding: '10px 8px 5px', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const d: React.CSSProperties = { padding: '4px 8px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' };
  const rt: React.CSSProperties = { textAlign: 'right' };
  const numI: React.CSSProperties = { width: 90, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, textAlign: 'right', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
  const txtI: React.CSSProperties = { width: '100%', minWidth: 110, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' };
  const sel: React.CSSProperties = { padding: '6px 6px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12.5, background: '#fff', boxSizing: 'border-box' };
  const btnG: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };

  return (
    <div>
      {payeeType && <FilingChips clientId={clientId} kind={whtKind} year={year} month={month} value={whtFiling} onChange={setWhtFiling} refreshKey={data} />}
      {/* Filed-return documents for PND.3 / PND.53 — the same three slots PND.1 and SSO already had
          (the backend has always allowed them, only this panel never showed the card). Sits under
          the submission chips so it follows the selected filing, exactly like VAT. */}
      {payeeType && <FilingDocsCard clientId={clientId} filing={whtKind} year={year} month={month}
        monthName={MONTHS[month - 1]} filingNo={whtFiling === 'all' ? 0 : whtFiling} />}
      {payeeType && <PenaltyCard clientId={clientId} kind={whtKind} year={year} month={month} filingNo={whtFiling === 'all' ? 0 : whtFiling} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>{payeeType ? `PND.${pndCode}` : t('tx_wht')} · {MONTHS[month - 1]} {year}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('wht_pnd_hint')}</span>
        <div style={{ flex: 1 }} />
        <button onClick={addRow} disabled={busy} style={btnG}><i className="ti ti-plus" style={{ marginRight: 5 }} />{t('wht_add')}</button>
        {certifiable.length > 0 && !!data?.stampReady && (
          <label title={t('tx_tawi50_stamp_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: tawiStamp ? '#7C3AED' : 'var(--text-tertiary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={tawiStamp} onChange={e => setTawiStamp(e.target.checked)} style={{ cursor: 'pointer' }} />
            <i className="ti ti-rubber-stamp" style={{ fontSize: 13 }} aria-hidden />{t('tx_tawi50_stamp')}
          </label>
        )}
        {certifiable.length > 0 && (
          <button onClick={downloadTawi50} disabled={tawiBusy} title={t('tx_tawi50_hint')}
            style={{ ...btnG, border: '1px solid #7C3AED', color: '#7C3AED', background: 'transparent', cursor: tawiBusy ? 'default' : 'pointer', opacity: tawiBusy ? 0.6 : 1 }}>
            <i className={`ti ti-${tawiBusy ? 'loader-2' : 'file-certificate'}`} style={{ marginRight: 5, animation: tawiBusy ? 'spin 0.7s linear infinite' : undefined }} />
            {tawiBusy ? t('tx_tawi50_busy') : `${t('tx_tawi50')}${selHere.length ? ` (${selHere.length})` : ''}`}</button>
        )}
        <button onClick={downloadXls} disabled={!lines.length || xlsBusy} style={{ ...btnG, cursor: xlsBusy ? 'default' : 'pointer', opacity: xlsBusy ? 0.6 : 1 }}>
          <i className={`ti ti-${xlsBusy ? 'loader-2' : 'file-spreadsheet'}`} style={{ marginRight: 5, animation: xlsBusy ? 'spin 0.7s linear infinite' : undefined }} />{t('tx_export')}</button>
        {!!payeeType && (
          <button onClick={downloadPndPaper} disabled={!lines.length || pndBusy} title={t('tx_pnd_paper_hint')}
            style={{ ...btnG, border: '1px solid #0E7C66', color: '#0E7C66', background: 'transparent', cursor: pndBusy ? 'default' : 'pointer', opacity: pndBusy ? 0.6 : 1 }}>
            <i className={`ti ti-${pndBusy ? 'loader-2' : 'file-text'}`} style={{ marginRight: 5, animation: pndBusy ? 'spin 0.7s linear infinite' : undefined }} />
            {pndBusy ? t('tx_tawi50_busy') : t('tx_pnd_paper', { no: pndCode })}</button>
        )}
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
        : lines.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('wht_empty')}</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead><tr>
              <th style={{ ...h, width: 28, textAlign: 'center' }}><input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all" /></th>
              <th style={{ ...h, width: 30 }}>#</th>
              <th style={h}>{t('wht_payee')}</th>
              <th style={h}>{t('wht_taxid')}</th>
              <th style={{ ...h, width: 150 }}>{t('wht_type')}</th>
              <th style={{ ...h, width: 170 }}>{t('wht_income')}</th>
              <th style={{ ...h, width: 130 }}>{t('wht_pay_date')}</th>
              <th style={{ ...h, ...rt }}>{t('wht_gross')}</th>
              <th style={{ ...h, ...rt, width: 70 }}>{t('wht_rate')}</th>
              <th style={{ ...h, ...rt }}>{t('wht_amount')}</th>
              <th style={{ ...h, width: 30 }} />
            </tr></thead>
            <tbody>
              {lines.map((l: any, i: number) => (
                <tr key={l.id} style={{ background: selected.has(l.id) ? '#EFF6FF' : l.pnd === '3' ? '#FFFDF5' : undefined }}>
                  <td style={{ ...d, textAlign: 'center' }}><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSel(l.id)} /></td>
                  <td style={{ ...d, color: 'var(--text-tertiary)' }}>{i + 1}<div style={{ fontSize: 9, fontWeight: 700, color: l.pnd === '3' ? '#B45309' : '#1D4ED8' }}>PND.{l.pnd}</div></td>
                  {/* Picking a partner fills name + tax ID + ADDRESS in one patch. The address never
                      appears in this grid — it exists so Form 50 Tawi can print it — so the marker
                      below is the only place an operator can see it is missing. */}
                  <td style={d}>
                    {/* A line a supplier payment holds (batch 11 R5) keeps the payment's payee: picking a partner saves its
                        address and partner only — the 50 ทวิ prints the address (the server's payment_owned rule). */}
                    <PartnerPicker
                      value={l.payee_name || ''}
                      clientId={clientId}
                      placeholder={t('wht_payee')}
                      onCommit={v => editField(l.id, { payeeName: v })}
                      onPick={p => editField(l.id, l.payment ? { payeeAddress: p.address, partnerId: p.partnerId } : { payeeName: p.name, payeeTaxId: p.taxId, payeeAddress: p.address, partnerId: p.partnerId, payeeType: p.entityType })}
                    />
                    {/* Batch 11 R5: the supplier payment that holds the line, or the reversed one that let it go. */}
                    {l.payment ? <div title={t('tx_wpay_owned')} style={{ fontSize: 10.5, fontWeight: 800, color: '#1D4ED8', whiteSpace: 'nowrap', marginTop: 2 }} data-wht-payment={l.id}><i className="ti ti-link" style={{ fontSize: 10, marginRight: 2 }} />{t('tx_wpay_chip', { payNo: l.payment.payNo })}</div>
                      : l.payment_role === 'released' ? <div style={{ fontSize: 10.5, fontWeight: 800, color: '#92400E', whiteSpace: 'nowrap', marginTop: 2 }} data-wht-released={l.id}>{t('tx_wpay_released')}</div> : null}
                  </td>
                  {l.payment ? (
                    /* Held by a payment: its figures are the payment's — read here, changed by reversing the payment. */
                    <>
                      <td style={d}>{l.payee_tax_id}</td>
                      <td style={d}>{l.payee_type === 'individual' ? t('wht_individual') : t('wht_company')}</td>
                      <td style={d}>{l.income_type || '—'}</td>
                      <td style={d}>{l.pay_date ? String(l.pay_date).split('-').reverse().join('/') : '—'}</td>
                      <td style={{ ...d, ...rt }}>{money(l.gross)}</td>
                      <td style={{ ...d, ...rt }}>{l.rate}</td>
                    </>
                  ) : (<>
                  <td style={d}><input key={`tx${l.id}:${l.payee_tax_id}`} defaultValue={l.payee_tax_id} onBlur={e => e.target.value !== l.payee_tax_id && editField(l.id, { payeeTaxId: e.target.value })} style={{ ...txtI, minWidth: 120 }} /></td>
                  <td style={d}><select value={l.payee_type} onChange={e => editField(l.id, { payeeType: e.target.value })} style={sel}><option value="individual">{t('wht_individual')}</option><option value="company">{t('wht_company')}</option></select></td>
                  <td style={d}><select value={l.income_type} onChange={e => { const ty = types.find(x => x.label === e.target.value); editField(l.id, { incomeType: e.target.value, rate: ty ? ty.rate : l.rate }); }} style={{ ...sel, width: '100%' }}>{!types.some(x => x.label === l.income_type) && <option value={l.income_type}>{l.income_type || '—'}</option>}{types.map((x, j) => <option key={j} value={x.label}>{x.label} ({x.rate}%)</option>)}</select></td>
                  {/* One date: it is both the document issue date and the payment date on Form 50 ทวิ
                      (owner 2026-08-05). Defaults to today when the line is created. */}
                  <td style={d}><input type="date" key={`pd${l.id}:${l.pay_date || ''}`} defaultValue={l.pay_date || ''}
                    onChange={e => e.target.value !== (l.pay_date || '') && editField(l.id, { payDate: e.target.value })}
                    title={t('wht_pay_date_tip')} style={{ ...txtI, minWidth: 118 }} /></td>
                  <td style={{ ...d, ...rt }}><input key={`g${l.id}:${l.gross}`} defaultValue={l.gross} onBlur={e => Number(e.target.value) !== l.gross && editMoney(l, { gross: Number(e.target.value) || 0 })} style={numI} inputMode="decimal" /></td>
                  <td style={{ ...d, ...rt }}><input key={`r${l.id}:${l.rate}`} defaultValue={l.rate} onBlur={e => Number(e.target.value) !== l.rate && editMoney(l, { rate: Number(e.target.value) || 0 })} style={{ ...numI, width: 54 }} inputMode="decimal" /></td>
                  </>)}
                  <td style={{ ...d, ...rt, fontWeight: 700 }}>{money(l.wht)}
                    {/* Two different facts, never merged into one word (owner 2026-09-09): the money
                        arriving from the client, and the tax going out to the Revenue Department.
                        The green Paid line is the one that answers "has this been dealt with". */}
                    {/* The client's money arriving. The old stamp here showed collected_at — the
                        moment an operator clicked Match — which reads like a payment time and is
                        not one; that audit detail now lives in the tooltip. */}
                    {!!l.client_paid_on && (
                      <div title={Number(l.paid_at) > 0 ? `${t('tx_wht_paid')}: ${fmtDateTime(l.paid_at)}` : ''}
                        style={{ fontSize: 10.5, fontWeight: 800, color: '#15803D', whiteSpace: 'nowrap' }}>
                        <i className="ti ti-cash-banknote" style={{ fontSize: 10, marginRight: 2 }} />{t('tx_wht_client_paid')} · {String(l.client_paid_on).split('-').reverse().join('/')}
                      </div>
                    )}
                    {!!l.remitted_on && (
                      <div title={t('tx_wht_remitted_tip')} style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                        <i className="ti ti-building-bank" style={{ fontSize: 10, marginRight: 2 }} />{t('tx_wht_remitted')} · {String(l.remitted_on).split('-').reverse().join('/')}
                      </div>
                    )}
                  </td>
                  <td style={{ ...d, textAlign: 'center' }}>
                    {l.payment
                      ? <i className="ti ti-lock" title={t('tx_wpay_owned')} style={{ color: 'var(--text-tertiary)', fontSize: 14 }} />
                      : Number(l.paid_at) > 0
                      ? <i className="ti ti-lock" title={t('tx_wht_paid_locked')} style={{ color: 'var(--text-tertiary)', fontSize: 14 }} />
                      : <button onClick={() => delRow(l.id)} title={t('delete')} style={{ border: 'none', background: 'transparent', color: '#C2410C', cursor: 'pointer' }}><i className="ti ti-trash" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {([['3', tot.pnd3], ['53', tot.pnd53]] as const).filter(([p]) => !payeeType || p === pndCode).map(([p, s]) => (
                <tr key={p} style={{ background: 'var(--bg-surface)', fontWeight: 700 }}>
                  <td style={d} colSpan={6}>{t('wht_total', { pnd: p, n: s.count })}</td>
                  <td style={{ ...d, ...rt }}>{money(s.gross)}</td>
                  <td style={d} />
                  <td style={{ ...d, ...rt }}>{money(s.wht)}</td>
                  <td style={d} />
                </tr>
              ))}
            </tfoot>
          </table>
        )}
    </div>
  );
}

// ── PND.1ก — annual payroll-WHT summary (year-level; aggregates the 12 monthly PND.1) ─────────
function Pnd1kPanel({ clientId, year }: { clientId: string; year: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const load = async () => { setLoading(true); try { setData(await api.pnd1Annual(clientId, year)); } catch { setData(null); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [clientId, year]); // eslint-disable-line
  const lines: any[] = data?.lines || [];
  const tot = data?.totals || { count: 0, annualIncome: 0, annualWht: 0 };
  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see th at the top of the file). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see th there too).
  const h: React.CSSProperties = { padding: '10px 10px 5px', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const d: React.CSSProperties = { padding: '6px 10px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' };
  const rt: React.CSSProperties = { textAlign: 'right' };
  const btnG: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' };
  return (
    <div style={{ marginTop: 28, borderTop: '2px solid var(--border)', paddingTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('pnd1k_title')} · {year}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pnd1k_hint')}</span>
        <div style={{ flex: 1 }} />
        <DlBtn onRun={() => api.pnd1AnnualExport(clientId, year)} disabled={!lines.length} style={btnG}><i className="ti ti-file-spreadsheet" style={{ marginRight: 5 }} />{t('tx_export')}</DlBtn>
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 20 }} /></div>
        : lines.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pnd1k_empty')}</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead><tr>
              <th style={{ ...h, width: 34 }}>#</th><th style={h}>{t('sso_emp_id')}</th><th style={h}>{t('sso_name')}</th>
              <th style={{ ...h, ...rt, width: 80 }}>{t('pnd1k_months')}</th><th style={{ ...h, ...rt }}>{t('pnd1k_income')}</th><th style={{ ...h, ...rt }}>{t('pnd1k_wht')}</th>
            </tr></thead>
            <tbody>
              {lines.map((l: any, i: number) => (
                <tr key={i}>
                  <td style={{ ...d, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                  <td style={d}>{l.id_number || '—'}</td><td style={d}>{l.name}</td>
                  <td style={{ ...d, ...rt }}>{l.months}</td><td style={{ ...d, ...rt }}>{money(l.annualIncome)}</td><td style={{ ...d, ...rt, fontWeight: 700 }}>{money(l.annualWht)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg-surface)', fontWeight: 700 }}>
                <td style={d} colSpan={3}>{t('sso_company_total')} · {tot.count} {t('sso_emps')}</td>
                <td style={d} /><td style={{ ...d, ...rt }}>{money(tot.annualIncome)}</td><td style={{ ...d, ...rt }}>{money(tot.annualWht)}</td>
              </tr>
            </tfoot>
          </table>
        )}
    </div>
  );
}

// ── Placeholder for filing types not yet built (Property Tax, Personal Income Tax) ──

// ── Main view ────────────────────────────────────────────────────────────────
// `initialView` / `noPicker` (owner 2026-10-13): the company workspace mounts this view one filing per
// submenu (VAT, WHT, PND…), so it needs to open on a given filing — and when the client fetch fails it
// must show an error, not the cross-client picker, which would let a user walk out of the company
// they are working in. Both default to today's behaviour.
export type TaxViewInitial = 'vat' | 'pnd1' | 'pnd3' | 'pnd53' | 'sso' | 'summary' | 'status';
export function TaxView({ clientId, initialView, noPicker }: { clientId?: string; initialView?: TaxViewInitial; noPicker?: boolean } = {}) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [clientErr, setClientErr] = useState('');
  const [year, setYear] = useState(() => Number(params.get('year')) || new Date().getFullYear());
  const [refreshing, setRefreshing] = useState(false);
  const sigRef = useRef('');

  // Persist the chosen client (+year) in the URL so a browser refresh (F5) stays on
  // the same client instead of bouncing back to the picker. Skipped when embedded
  // (Client Dashboard passes a fixed clientId and owns its own URL).
  const pickClient = (c: Client | null) => {
    setClient(c);
    if (!c) setLines([]);
    if (!clientId) { if (c) params.set('client', c.id); else params.delete('client'); setParams(params, { replace: true }); }
  };
  useEffect(() => { if (!clientId && client) { params.set('year', String(year)); setParams(params, { replace: true }); } }, [year]); // eslint-disable-line
  const [tab, setTab] = useState<'summary' | number>(new Date().getMonth() + 1);   // default to the CURRENT month
  // Right-side filing selector (dropdown): the firm's tax service lines for this client's month.
  type MView = 'vat' | 'pnd1' | 'pnd3' | 'pnd53' | 'property_tax' | 'personal_income_tax' | 'sso' | 'summary' | 'status';
  const [mView, setMView] = useState<MView>(initialView || 'vat');
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lightbox, setLightbox] = useState<{ id: string; mime: string }[] | null>(null);
  const [credit, setCredit] = useState<any[]>([]);
  const [creditAdd, setCreditAdd] = useState<any[][]>([]);
  const [creditOpen, setCreditOpen] = useState<{ amount: number; ym: string }>({ amount: 0, ym: '' });

  const load = async (c = client, y = year) => {
    if (!c) return;
    setLoading(true); setErr('');
    try { const d: any = await api.taxGetYear(c.id, y); setClient(d.client); setLines(d.lines || []); setCredit(d.credit || []); setCreditAdd(d.creditAdd || []); setCreditOpen(d.creditOpening || { amount: 0, ym: '' }); setSaveStatus('idle'); }
    catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  // Re-fetch without the loading spinner (no flicker) — used by the manual refresh
  // button, after an upload, and by the live poll below.
  const silentLoad = async () => {
    const c = client; if (!c) return;
    try { const d: any = await api.taxGetYear(c.id, year); setClient(d.client); setLines(d.lines || []); setCredit(d.credit || []); setCreditAdd(d.creditAdd || []); setCreditOpen(d.creditOpening || { amount: 0, ym: '' }); } catch { /* keep current */ }
  };
  const refresh = async () => {
    if (refreshing) return;                       // ignore repeat clicks while spinning
    setRefreshing(true);
    const started = Date.now();
    await silentLoad();
    const left = 650 - (Date.now() - started);    // keep the spinner visible long enough to be seen
    if (left > 0) await new Promise(r => setTimeout(r, left));
    setRefreshing(false);
  };
  useEffect(() => { if (client) load(); }, [client?.id, year]); // eslint-disable-line
  // Restore the client on mount: from the embed prop, else the URL ?client=.
  useEffect(() => {
    const cid = clientId || params.get('client');
    if (cid && !client) api.taxGetYear(cid, year).then((d: any) => setClient(d.client)).catch((e: any) => setClientErr(String(e?.message || 'error')));
  }, [clientId]); // eslint-disable-line
  // Reset the signature baseline when the client/year changes (re-seeded by the poll below).
  useEffect(() => { sigRef.current = ''; }, [client?.id, year]);
  // Live auto-refresh: poll a composite signature (line count + latest updated_at). When it
  // changes externally — a Telegram upload added a line, OR a TG edit flipped a section /
  // changed a date / regenerated a record — and the user isn't mid-edit, silently re-load.
  useEffect(() => {
    if (!client) return;
    const h = setInterval(async () => {
      try {
        const s: any = await api.taxSig(client.id, year);
        const sig = s?.sig != null ? String(s.sig) : (typeof s?.count === 'number' ? String(s.count) : '');
        if (!sig) return;
        if (sigRef.current === '') { sigRef.current = sig; return; }   // seed the baseline
        if (sig !== sigRef.current) {
          sigRef.current = sig;                                        // adopt the new state
          const ae = document.activeElement as HTMLElement | null;
          const editing = !!ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName);
          if (!editing) silentLoad();
        }
      } catch { /* ignore */ }
    }, 20000);
    return () => clearInterval(h);
  }, [client?.id, year]); // eslint-disable-line

  const change = (id: string, patch: Partial<Line>) => setLines(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  // (Review 2026-11-03) A line can post itself after this grid loaded it (server services/autoPost.ts), and a refused PATCH pages the admins and loses the cell: asked just before each save,
  // and said here. The row turns read-only, as it would on the next poll. Unanswered, the save itself answers.
  const stillOpen = async (id: string): Promise<boolean> => {
    let inBooks: InBooksRow[] = [];
    try { inBooks = (await api.taxLineInBooks(id))?.inBooks || []; } catch { return true; }
    if (!inBooks.length) return true;
    change(id, { inBooks });
    setErr(t('tx_ib_tip', { docNos: inBooks.map(x => x.docNo).join(', ') }));
    setSaveStatus('idle');
    void silentLoad();   // what the row holds now, not what was typed over it
    return false;
  };
  // ⛔ These saves name no breakdown (baseZero / baseExempt / baseOutscope — this grid shows none): the server keeps the
  // line's own while amount0 is unchanged (services/taxLines.ts keepBreakdown). Sending one here would be a word that it changed.
  const saveLine = async (id: string) => {
    const l = lines.find(x => x.id === id); if (!l || lineInBooks(l)) return;   // batch 11 R3: in the books — never sent
    if (!await stillOpen(id)) return;
    setSaveStatus('saving'); setErr('');
    try { await api.taxUpdateLine(id, { lineDate: l.line_date, docNo: l.doc_no, partyName: l.party_name, partyTaxId: l.party_tax_id, branchHead: l.branch_head, branchNo: l.branch_no, amount: l.amount, amount0: l.amount0 || 0, vat: l.vat, withhold: l.withhold }); setSaveStatus('saved'); }
    catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };
  // Save with the patch applied DIRECTLY — not via state + a later read. The partner picker commits
  // from a 120ms-delayed blur handler, so "update state, then save whatever state holds" would race
  // React's flush and sometimes write the pre-pick values back over the pick.
  const patchAndSave = async (id: string, patch: Partial<Line> & { partnerId?: string }) => {
    const l = lines.find(x => x.id === id); if (!l || lineInBooks(l)) return;   // batch 11 R3: in the books — never sent
    const nl = { ...l, ...patch };
    setLines(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
    if (!await stillOpen(id)) return;
    setSaveStatus('saving'); setErr('');
    try {
      await api.taxUpdateLine(id, {
        lineDate: nl.line_date, docNo: nl.doc_no, partyName: nl.party_name, partyTaxId: nl.party_tax_id,
        branchHead: nl.branch_head, branchNo: nl.branch_no, amount: nl.amount, amount0: nl.amount0 || 0,
        vat: nl.vat, withhold: nl.withhold, ...(patch.partnerId !== undefined ? { partnerId: patch.partnerId } : {}),
      });
      setSaveStatus('saved');
    } catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };
  // Which INPUT sub-table a freshly-added blank line belongs to ('vat' | 'novat'). A new line has
  // vat=0, which would otherwise dump it straight into the "no VAT" table even when the operator
  // clicked Add on the VAT table — the tag pins it until a real VAT value (or a reload) decides.
  const inputHome = useRef<Record<string, 'vat' | 'novat'>>({});
  // Which submission the operator is working in ('all' = combined view; new lines then land in
  // the month's open filing server-side).
  const [vatFiling, setVatFiling] = useState<'all' | number>('all');
  const [vatFilingRefresh, setVatFilingRefresh] = useState(0);
  const [vatMailOpen, setVatMailOpen] = useState(false);   // Monthly VAT Summary composer (manual send)
  // Close the composer whenever the client/year/month/view changes — otherwise it would silently
  // rebind to the new month (discarding operator edits) or reopen stale on return to the VAT view.
  useEffect(() => { setVatFiling('all'); setVatMailOpen(false); }, [client?.id, year, tab, mView]);   // eslint-disable-line
  const addLine = async (section: 'output' | 'input', home?: 'vat' | 'novat') => {
    if (!client || typeof tab !== 'number') return;
    setSaveStatus('saving'); setErr('');
    try {
      const r: any = await api.taxAddLine(client.id, { year, month: tab, section, branchNo: '00000', filingNo: vatFiling === 'all' ? undefined : vatFiling });
      if (section === 'input' && home) inputHome.current[r.id] = home;
      setLines(prev => [...prev, { id: r.id, month: tab, section, line_date: '', doc_no: '', party_name: '', party_tax_id: '', branch_head: 1, branch_no: '00000', amount: 0, amount0: 0, vat: 0, withhold: 0, total: 0 }]);
      setSaveStatus('saved');
    } catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };
  const delLine = async (id: string) => {
    const dl = lines.find(x => x.id === id); if (dl && lineInBooks(dl)) return;   // batch 11 R3: the button is not drawn
    setSaveStatus('saving'); setErr('');
    try { await api.taxDeleteLine(id); setLines(prev => prev.filter(l => l.id !== id)); setSaveStatus('saved'); } catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };
  const reviewLine = async (id: string) => {
    const l = lines.find(x => x.id === id); if (!l) return;
    setLines(prev => prev.map(x => x.id === id ? { ...x, confidence: '' } : x)); // optimistic: clear flag
    // Batch 11 R3: a line in the books sends the review tick alone (the server writes the flag and nothing else).
    if (lineInBooks(l) || !await stillOpen(id)) { try { await api.taxUpdateLine(id, { reviewOnly: true, confidence: '' }); } catch (e: any) { setErr(e.message); } return; }
    try { await api.taxUpdateLine(id, { lineDate: l.line_date, docNo: l.doc_no, partyName: l.party_name, partyTaxId: l.party_tax_id, branchHead: l.branch_head, branchNo: l.branch_no, amount: l.amount, amount0: l.amount0 || 0, vat: l.vat, withhold: l.withhold, confidence: '' }); }
    catch (e: any) { setErr(e.message); }
  };
  const [ocrBusy, setOcrBusy] = useState<'output' | 'input' | ''>('');
  // Options for "move selected lines to another submission": every filing seen in this month's
  // lines + Normal + the next new Additional. The server refuses locked targets.
  const monthFilingOptions = React.useMemo(() => {
    const seen = new Set<number>([0]);
    for (const l of lines) if (l.month === tab) seen.add(Number((l as any).filing_no || 0));
    const max = Math.max(...Array.from(seen));
    seen.add(max + 1);
    return Array.from(seen).sort((a, b) => a - b).map(no => ({ no, label: no === 0 ? t('tx_fl_normal') : `${t('tx_fl_add')} ${no}` }));
  }, [lines, tab, t]);
  const moveToFiling = async (ids: string[], no: number) => {
    try { await api.taxMoveLinesFiling(client.id, ids, no); setVatFilingRefresh(x => x + 1); await silentLoad(); }
    catch (e: any) { alert(e.message); }
  };
  const ocrUpload = async (section: 'output' | 'input', files: FileList) => {
    if (!client || typeof tab !== 'number') return;
    setOcrBusy(section); setErr(''); setSaveStatus('saving');
    try {
      const r: any = await api.taxOcr(client.id, { year, month: tab, section, filingNo: vatFiling === 'all' ? undefined : vatFiling }, files);
      setSaveStatus('saved');
      await silentLoad();   // auto-refresh from the server (no flicker)
      const notes: string[] = [];
      if (r.updated > 0) notes.push(t('tx_ocr_updated', { n: r.updated }));
      const incomplete = ((r.lines as any[]) || []).filter(x => x.missing && x.missing.length).length;
      if (incomplete > 0) notes.push(t('tx_ocr_incomplete', { n: incomplete }));
      if (r.errors && r.errors.length) notes.push(t('tx_ocr_partial', { n: r.errors.length }) + ' — ' + r.errors.join('; '));
      // Batch 11 R3 (W2-6): a scan that re-read a line already in the books was not applied — said, line by line.
      for (const x of ((r.lines as any[]) || []).filter(x => x && x.applied === false)) {
        notes.push(t('tx_ll_ocr_not_applied', { docNo: x.doc_no || '', docNos: ((x.inBooks || []) as any[]).map(e => e.docNo).join(', ') || x.noteNo || '' }));
      }
      if (notes.length) setErr(notes.join(' · '));
    } catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
    finally { setOcrBusy(''); }
  };

  // Bulk-flip every line in this month's section to the other side (Output ↔ Input).
  const moveAll = async (from: 'output' | 'input') => {
    if (!client || typeof tab !== 'number') return;
    const n = lines.filter(l => l.month === tab && l.section === from).length;
    if (!n || lines.some(l => l.month === tab && l.section === from && lineInBooks(l))) return;   // batch 11 R3: disabled
    const toLabel = from === 'output' ? t('tx_input') : t('tx_output');
    if (!window.confirm(t('tx_move_confirm', { n, to: toLabel }))) return;
    setSaveStatus('saving'); setErr('');
    try { await api.taxMoveSection(client.id, year, tab, from); await silentLoad(); setSaveStatus('saved'); }
    catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };
  // Move only the selected lines to the other side.
  const moveSelected = async (ids: string[], from: 'output' | 'input') => {
    if (!client || !ids.length) return;
    setSaveStatus('saving'); setErr('');
    try { await api.taxMoveLines(client.id, ids, from === 'output' ? 'input' : 'output'); await silentLoad(); setSaveStatus('saved'); }
    catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
  };

  // Regenerate a line from its stored photo(s): re-read ALL images, overwrite the record +
  // its bookkeeping items. If the AI is unsure, surface it (amber row) or, for an ambiguous
  // date, pop the date picker — just like Telegram.
  const [regeneratingId, setRegeneratingId] = useState('');
  const [dateChoice, setDateChoice] = useState<{ lineId: string; dates: string[] } | null>(null);
  const regenerate = async (id: string) => {
    const rl = lines.find(x => x.id === id);
    if (!client || regeneratingId || (rl && lineInBooks(rl))) return;   // batch 11 R3: the button is not drawn
    if (!window.confirm(t('tx_regen_confirm'))) return;
    setRegeneratingId(id); setErr(''); setSaveStatus('saving');
    try {
      const r: any = await api.taxRegenerate(client.id, id);
      await silentLoad(); setSaveStatus('saved');
      if (r.missing && r.missing.length) setErr(t('tx_ocr_incomplete', { n: 1 }));
      if (r.dateCandidates && r.dateCandidates.length > 1) setDateChoice({ lineId: id, dates: r.dateCandidates });
    } catch (e: any) { setErr(e.message); setSaveStatus('idle'); }
    finally { setRegeneratingId(''); }
  };
  const pickRegenDate = async (lineId: string, iso: string) => {
    setDateChoice(null);
    const l = lines.find(x => x.id === lineId); if (!l || !await stillOpen(lineId)) return;
    setLines(prev => prev.map(x => x.id === lineId ? { ...x, line_date: iso } : x));   // optimistic
    try { await api.taxUpdateLine(lineId, { lineDate: iso, docNo: l.doc_no, partyName: l.party_name, partyTaxId: l.party_tax_id, branchHead: l.branch_head, branchNo: l.branch_no, amount: l.amount, amount0: l.amount0 || 0, vat: l.vat, withhold: l.withhold }); }
    catch (e: any) { setErr(e.message); }
  };

  // live monthly summary from the loaded lines
  const monthly = useMemo(() => {
    const blank = () => ({ amount: 0, amount0: 0, vat: 0, withhold: 0, total: 0 });
    const arr = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, output: blank(), input: blank(), tax: 0 }));
    for (const l of lines) {
      const m = arr[l.month - 1]; if (!m) continue;
      if ((l as any).calc_error) continue;   // quarantined rows stay out of the totals (same rule as filings/exports)
      const tgt = l.section === 'input' ? m.input : m.output;
      tgt.amount = r2(tgt.amount + l.amount); tgt.amount0 = r2(tgt.amount0 + (Number(l.amount0) || 0)); tgt.vat = r2(tgt.vat + l.vat); tgt.withhold = r2(tgt.withhold + l.withhold); tgt.total = r2(tgt.total + l.total);
    }
    arr.forEach(m => { m.tax = r2(Math.max(0, m.output.vat - m.input.vat)); });
    return arr;
  }, [lines]);

  if (!client && clientId && noPicker) {
    // Embedded in a dedicated company screen: a loader while the fetch runs, an error card when it
    // failed — never the picker (see the note on the props above).
    return clientErr
      ? <div style={{ margin: 16, fontSize: 12.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', padding: '8px 12px', borderRadius: 8 }}>{t('aw_tax_load_failed')}: {clientErr}</div>
      : <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  }
  if (!client) return clientId
    ? <ClientPicker onPick={pickClient} />
    : (
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: '0 0 360px', borderRight: '1px solid var(--border)', overflow: 'auto' }}><ClientPicker onPick={pickClient} /></div>
        <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}><TaxFilingsPanel /></div>
      </div>
    );

  const hasVat = (client.has_vat ?? 1) !== 0;
  // Each table is sorted by invoice date ascending (1st → last day); blank dates go last.
  const monthLines = (section: 'output' | 'input') => lines
    .filter(l => vatFiling === 'all' || Number(l.filing_no || 0) === vatFiling)
    .filter(l => l.month === tab && l.section === section)
    .sort((a, b) => {
      const ad = a.line_date || '', bd = b.line_date || '';
      if (ad === bd) return 0;
      if (!ad) return 1; if (!bd) return -1;
      return ad < bd ? -1 : 1;
    });
  const tabBtn = (k: 'summary' | number, label: string) => (
    <button key={String(k)} onClick={() => setTab(k)} style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `2.5px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
  );
  const years = [year - 1, year, year + 1, year + 2].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* client header. A bubble, as every page toolbar is (Calem, report 76a2c198, 2nd opening — see pageToolbar):
          10px corners and a --border-strong line all the way round where a --border line ran under it alone,
          PAGE_INSET in from the page's edges — the one page gutter the whole system stands in by, 15px, where this
          page's own was 18 (that report's 5th opening: "Move the left and right page spacing in from the edges to 15
          px on every page of the SLF system"; see PAGE_INSET in tabPalette) — with a gap above and below it. The
          months stack under it in a bubble of their own (pageToolbarUnder) on the same 15, so one gap parts the two
          and another parts the months from the panel below, which takes that 15 as well. The page's own header, with
          the logo and the Tax tabs, keeps its full-width line. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', ...pageToolbar(), background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
        {!clientId && <button onClick={() => pickClient(null)} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}><i className="ti ti-arrow-left" style={{ marginRight: 5 }} />{t('tx_change_client')}</button>}
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{client.company_name || client.client_name}</div>
          {/* The SSO e-filing login name rides on the end of this same line, after Branch, in the
              very same type — read-only, and only while an SSO month is open. Saved in Edit Client
              → Employees, never from here. */}
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
            {t('tx_party_tax')}: {client.tax_id || '—'} · {t('tx_branch')}: {client.branch_no || '00000'}
            {mView === 'sso' && typeof tab === 'number' && <>{' · '}{t('tx_sso_username')}: {client.sso_username || t('tx_sso_not_set')}</>}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {saveStatus !== 'idle' && (
          <span style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, color: saveStatus === 'saving' ? 'var(--text-tertiary)' : '#1D9E75' }}>
            <i className={`ti ti-${saveStatus === 'saving' ? 'loader-2' : 'circle-check'}`} />{saveStatus === 'saving' ? t('tx_saving') : t('tx_saved')}
          </span>
        )}
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('tx_year')}</label>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={refresh} disabled={refreshing} title={t('refresh')}
          style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border-strong)', background: refreshing ? 'var(--bg-active)' : 'var(--bg-app)', color: refreshing ? 'var(--accent)' : 'var(--text-secondary)', cursor: refreshing ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-refresh" style={{ fontSize: 16, ...(refreshing ? { display: 'inline-block', animation: 'spin 0.7s linear infinite' } : {}) }} /></button>
      </div>
      {/* tabs — Summary and the twelve months, stacked under the client bar in a bubble of their own. */}
      <div style={{ display: 'flex', gap: 2, padding: '0 12px', ...pageToolbarUnder(), background: 'var(--bg-surface)', overflowX: 'auto' }}>
        {tabBtn('summary', t('tx_summary'))}
        {MONTHS.map((m, i) => tabBtn(i + 1, m))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: `16px ${PAGE_INSET}px` }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
        {regeneratingId && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}><i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />{t('tx_regen_loading')}</div>}
        {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
          : tab === 'summary' ? <><Summary monthly={monthly} hasVat={hasVat} credit={credit} /><Pnd1kPanel clientId={client.id} year={year} /></>
          : (
            <>
              {/* Filing selector (dropdown): one of the firm's tax service lines, per month. */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('tx_filing')}</label>
                <select value={mView} onChange={e => setMView(e.target.value as MView)}
                  style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--accent)', cursor: 'pointer', minWidth: 240 }}>
                  {([['vat', 'VAT'], ['pnd1', 'PND.1'], ['pnd3', 'PND.3'], ['pnd53', 'PND.53'], ['sso', t('tx_sso')], ['summary', t('tx_summary')], ['personal_income_tax', t('tx_pit')], ['status', t('tx_filing_status')]] as const).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
                {/* VAT (bookkeeping) year export lives with the VAT filing — not perpetually in the header — so it
                    no longer competes with each filing panel's own download. */}
                {mView === 'vat' && (
                  <DlBtn onRun={() => api.taxExport(client.id, year, vatFiling === 'all' ? undefined : vatFiling)} title={vatFiling === 'all' ? t('tx_export') : `${t('tx_export')} — ${vatFiling === 0 ? t('tx_fl_normal') : t('tx_fl_add') + ' ' + vatFiling}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', background: '#1D7C4D', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                    <i className="ti ti-file-spreadsheet" style={{ fontSize: 15 }} />{t('tx_export')}</DlBtn>
                )}
              </div>
              {mView === 'status' ? <FilingStatusPanel clientId={client.id} year={year} />
                : mView === 'pnd3' ? (<>
                    {typeof tab === 'number' && <ClientPaidStamp clientId={client.id} year={year} month={tab} taxKey="wht" />}
                    <WhtPanel clientId={client.id} year={year} month={tab as number} payeeType="individual" />
                  </>)
                : mView === 'pnd53' ? (<>
                    {typeof tab === 'number' && <ClientPaidStamp clientId={client.id} year={year} month={tab} taxKey="wht" />}
                    <WhtPanel clientId={client.id} year={year} month={tab as number} payeeType="company" />
                  </>)
                : mView === 'sso' ? (<>
                    {typeof tab === 'number' && <FilingDocsCard clientId={client.id} filing="sso" year={year} month={tab} monthName={MONTHS[tab - 1]} />}
                    {typeof tab === 'number' && <PenaltyCard clientId={client.id} kind="sso" year={year} month={tab} />}
                    {typeof tab === 'number' && <ClientPaidStamp clientId={client.id} year={year} month={tab} taxKey="sso" />}
                    <SsoPanel clientId={client.id} year={year} month={tab as number} />
                  </>)
                : mView === 'summary' ? <TaxSummaryPanel clientId={client.id} clientName={client.company_name || client.client_name} year={year} month={typeof tab === 'number' ? tab : (new Date().getMonth() + 1)} />
                : mView === 'pnd1' ? (<>
                    {typeof tab === 'number' && <FilingDocsCard clientId={client.id} filing="pnd1" year={year} month={tab} monthName={MONTHS[tab - 1]} />}
                    {typeof tab === 'number' && <PenaltyCard clientId={client.id} kind="pnd1" year={year} month={tab} />}
                    {typeof tab === 'number' && <ClientPaidStamp clientId={client.id} year={year} month={tab} taxKey="pnd1" />}
                    <Pnd1Panel clientId={client.id} year={year} month={tab as number} />
                  </>)
                : mView === 'personal_income_tax' ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
                      <i className="ti ti-info-circle" style={{ color: '#B45309', marginTop: 1 }} />{t('tx_pit_draft_hint')}
                    </div>
                    <Pnd1kPanel clientId={client.id} year={year} />
                  </>)
                : <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
                      <i className="ti ti-info-circle" />{t('tx_autosave_hint')}
                    </div>
                    {hasVat && typeof tab === 'number' && (
                      <VatCreditCard entry={credit[tab - 1] || null} additional={creditAdd[tab - 1] || []} opening={creditOpen} monthName={MONTHS[tab - 1]} hasChain={credit.some(Boolean)}
                        onSaveOpening={async (amount, ym) => { await api.taxSetVatOpening(client.id, { amount, ym }); await silentLoad(); }}
                        onEmail={mView === 'vat' && (year < new Date().getFullYear() || (year === new Date().getFullYear() && tab <= new Date().getMonth() + 1))
                          ? () => setVatMailOpen(true) : undefined} />
                    )}
                    {vatMailOpen && typeof tab === 'number' && (
                      <VatSummaryEmailComposer clientId={client.id} year={year} month={tab} monthName={MONTHS[tab - 1]} onClose={() => setVatMailOpen(false)} />
                    )}
                    {hasVat && typeof tab === 'number' && mView === 'vat' && (<>
                      <FilingChips clientId={client.id} kind="vat" year={year} month={tab} value={vatFiling} onChange={v => { setVatFiling(v); setVatFilingRefresh(x => x + 1); }} refreshKey={vatFilingRefresh} />
                      <FilingDocsCard clientId={client.id} filing="vat" year={year} month={tab} monthName={MONTHS[tab - 1]} filingNo={vatFiling === 'all' ? 0 : vatFiling} />
                      {typeof tab === 'number' && <PenaltyCard clientId={client.id} kind="vat" year={year} month={tab} filingNo={vatFiling === 'all' ? 0 : vatFiling} />}
                      <ClientPaidStamp clientId={client.id} year={year} month={tab} taxKey="vat" />
                    </>)}
                    {(() => {
                      const bad = lines.filter(l => l.month === tab && (l as any).calc_error).length;
                      return bad ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, color: '#B42318', background: '#FDE8E8', border: '1px solid #F5B5B1', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                          <i className="ti ti-alert-octagon" />{t('tx_calc_bad_banner', { n: bad })}
                        </div>
                      ) : null;
                    })()}
                    <SectionTable title={t('tx_output')} accent="#2B6CB0" section="output" hasVat={hasVat} clientId={client.id} onPatchSave={patchAndSave} lines={monthLines('output')} onChange={change} onBlurSave={saveLine} onAdd={() => addLine('output')} onDelete={delLine} onUpload={f => ocrUpload('output', f)} uploading={ocrBusy === 'output'} onReview={reviewLine} onMoveAll={() => moveAll('output')} onViewImages={imgs => imgs.length && setLightbox(imgs)} onMoveSelected={ids => moveSelected(ids, 'output')} onRegenerate={regenerate} regeneratingId={regeneratingId} filingOptions={monthFilingOptions} onMoveToFiling={moveToFiling} />
                    {(() => {
                      // Input (purchase) is split into TWO tables for VAT clients: invoices WITH VAT,
                      // and a separate table for purchases WITHOUT VAT (vat = 0). WHT is untouched —
                      // both tables keep the Withhold column and all filings/summaries read the same
                      // underlying lines. Non-VAT clients keep the single table (everything is no-VAT).
                      const all = monthLines('input');
                      const common = { section: 'input' as const, hasVat, clientId: client.id, onPatchSave: patchAndSave, onChange: change, onBlurSave: saveLine, onDelete: delLine, uploading: ocrBusy === 'input', onReview: reviewLine, onViewImages: (imgs: any) => imgs.length && setLightbox(imgs), onMoveSelected: (ids: string[]) => moveSelected(ids, 'input'), onRegenerate: regenerate, regeneratingId, onUpload: (f: FileList | null) => ocrUpload('input', f), onMoveAll: () => moveAll('input'), filingOptions: monthFilingOptions, onMoveToFiling: moveToFiling };
                      if (!hasVat) return <SectionTable title={t('tx_input')} accent="#1D9E75" lines={all} onAdd={() => addLine('input')} {...common} />;
                      const inVat = (l: Line) => Number(l.vat) !== 0 || inputHome.current[l.id] === 'vat';
                      return (<>
                        <SectionTable title={t('tx_input')} accent="#1D9E75" lines={all.filter(inVat)} onAdd={() => addLine('input', 'vat')} {...common} />
                        <SectionTable title={t('tx_input_novat')} accent="#B45309" lines={all.filter(l => !inVat(l))} onAdd={() => addLine('input', 'novat')} {...common} />
                      </>);
                    })()}
                  </>}
            </>
          )}
      </div>
      {lightbox && <ImageLightbox images={lightbox} onClose={() => setLightbox(null)} />}
      {dateChoice && <DateChoiceModal dates={dateChoice.dates} onPick={iso => pickRegenDate(dateChoice.lineId, iso)} onClose={() => setDateChoice(null)} />}
    </div>
  );
}

// ── Standalone page (/m/tax) ─────────────────────────────────────────────────
// ── Cross-client monthly VAT / Withholding management (the right-hand panel) ──
function TaxFilingsPanel() {
  const { t } = useTranslation();
  // vat + wht auto-sync from scanned invoices; the rest are manual cross-client tracking lists.
  type FKind = 'vat' | 'wht' | 'pnd1' | 'pnd3' | 'pnd53' | 'property_tax' | 'personal_income_tax' | 'sso';
  const [kind, setKind] = useState<FKind>('vat');
  // vat/wht sync from scanned invoices; sso/pnd1 from payroll; pnd3/pnd53 from the WHT panels.
  const autoSync = !['property_tax', 'personal_income_tax'].includes(kind);
  const [year, setYear] = useState(new Date().getFullYear());
  const [sel, setSel] = useState<number | null>(null);
  const [overview, setOverview] = useState<any[]>([]);
  const [matrix, setMatrix] = useState<{ name: string; months: number[]; total: number }[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);

  const loadOverview = () => api.taxFilingsOverview(year, kind).then((d: any) => setOverview(d.months || [])).catch(() => {});
  const loadMatrix = () => api.taxFilingsYearMatrix(year, kind).then((d: any) => setMatrix(d.rows || [])).catch(() => setMatrix([]));
  const loadMonth = (m: number) => api.taxFilingsMonth(year, m, kind).then((d: any) => setRows(d.rows || [])).catch(() => {});
  // Switching the filing kind re-seeds a whole year server-side, which can take seconds — without
  // feedback the page looks dead (owner 2026-08-06). A logo overlay covers the switch and drops
  // away the moment every request settles.
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    if (sel == null) {
      setSwitching(true);
      Promise.allSettled([loadOverview(), loadMatrix()]).finally(() => setSwitching(false));
    }
  }, [year, kind, sel]); // eslint-disable-line
  useEffect(() => {
    if (sel != null) {
      setSwitching(true);
      Promise.allSettled([loadMonth(sel)]).finally(() => setSwitching(false));
    }
  }, [sel, kind, year]); // eslint-disable-line

  const taxLabel = kind === 'vat' ? t('txf_net_vat') : kind === 'wht' ? t('txf_wht_tax') : t('txf_tax');
  const years = [year - 2, year - 1, year, year + 1];
  const num = (v: any) => Number(v) || 0;
  const sumOf = (k: string) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const change = (id: string, patch: any) => setRows(p => p.map(r => r.id === id ? { ...r, ...patch } : r));
  const saveRow = async (id: string) => {
    const r = rows.find(x => x.id === id); if (!r) return;
    const data = kind === 'vat'
      ? { client_name: r.client_name, entry_date: r.entry_date, out_base: num(r.out_base), out_vat: num(r.out_vat), in_base: num(r.in_base), in_vat: num(r.in_vat), brought_forward: num(r.brought_forward), surcharge: num(r.surcharge), fines: num(r.fines) }
      : { client_name: r.client_name, doc_no: r.doc_no, entry_date: r.entry_date, revenue_amount: num(r.revenue_amount), tax_amount: num(r.tax_amount) };
    try { await api.taxFilingsUpdateRow(id, data); } catch {}
  };
  // Marking Filed now CAPTURES the snapshot server-side (VAT: live figures + chain brought-forward
  // frozen at this moment), so the month must refetch after the call — the optimistic flip alone
  // would keep showing pre-capture numbers while the stored row, PDF and Excel show the captured ones.
  // Filing freezes figures that go on a ภ.พ.30, so the server refuses ONCE (409) when lines read
  // off a scan have never been checked by a person — unsure readings, amounts that do not add up,
  // a tax ID failing its checksum. It is not a block: the lines are shown, and confirming files
  // them and records that they were read. Errors used to be swallowed here (`catch {}`), which
  // would have made the gate invisible.
  const toggleFiled = async (id: string, filed: boolean) => {
    change(id, { filed: filed ? 1 : 0 });
    try {
      await api.taxFilingsSetFiled(id, filed);
    } catch (e: any) {
      if (e?.status === 409 && e?.payload?.error === 'unreviewed_ocr_lines') {
        const hits: any[] = e.payload.hits || [];
        const n = hits.reduce((a, h) => a + h.lines.length, 0);
        const why = (r: string) => t(r === 'confidence' ? 'tx_r_confidence' : r === 'calc' ? 'tx_r_calc' : 'tx_r_taxid');
        const body = hits.flatMap(h => h.lines.slice(0, 12).map((l: any) =>
          `  • ${l.line_date || '?'}  ${l.doc_no || '(no doc no)'}  ${(l.party_name || '').slice(0, 28)}  ${Number(l.total).toLocaleString()}\n      ${l.reasons.map(why).join(' · ')}`)).join('\n');
        const more = n > hits.reduce((a, h) => a + Math.min(h.lines.length, 12), 0) ? `\n  … +${n - hits.reduce((a, h) => a + Math.min(h.lines.length, 12), 0)}` : '';
        if (window.confirm(`${t('tx_filed_unreviewed_title', { n })}\n\n${body}${more}\n\n${t('tx_filed_unreviewed_tail')}`)) {
          try { await api.taxFilingsSetFiled(id, filed, true); } catch (e2: any) { window.alert(e2?.message || 'Could not file'); }
        } else {
          change(id, { filed: filed ? 0 : 1 });               // put the tick back where it was
        }
      } else if (e?.message) { window.alert(e.message); }
    }
    if (sel != null) loadMonth(sel);
  };
  const delRow = async (id: string) => { try { await api.taxFilingsDeleteRow(id); } catch {} if (sel != null) loadMonth(sel); };
  const addRow = async () => { if (sel == null) return; try { await api.taxFilingsAddRow(year, sel, kind, {}); } catch {} loadMonth(sel); };
  const refresh = async () => { if (sel == null) return; setBusy(true); try { await api.taxFilingsRefresh(year, sel, kind); } catch {} await loadMonth(sel); setBusy(false); };
  const rateOf = (r: any) => r._rate !== undefined ? r._rate : (Number(r.revenue_amount) ? (Number(r.tax_amount) / Number(r.revenue_amount) * 100).toFixed(2) : '0');
  const saveRate = async (id: string) => {
    const r = rows.find(x => x.id === id); if (!r) return;
    const tax = Math.round(num(r.revenue_amount) * num(r._rate) / 100 * 100) / 100;
    setRows(p => p.map(x => x.id === id ? { ...x, tax_amount: tax, _rate: undefined } : x));
    try { await api.taxFilingsUpdateRow(id, { client_name: r.client_name, doc_no: r.doc_no, entry_date: r.entry_date, revenue_amount: num(r.revenue_amount), tax_amount: tax }); } catch {}
  };

  const ddl: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };

  return (
    <div style={{ padding: '18px 20px', minWidth: 0, position: 'relative' }}>
      {switching && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'color-mix(in srgb, var(--bg-app) 78%, transparent)', backdropFilter: 'blur(2px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 160, gap: 14 }}>
          <img className="app-logo" src="/logo.png" alt="" style={{ width: 64, height: 64, objectFit: 'contain', animation: 'ddPulse 1.1s ease-in-out infinite' }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.9s linear infinite' }} />{t('txf_loading')}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('txf_manage_title')}</div>
        <div style={{ flex: 1 }} />
        <select value={kind} onChange={e => { setKind(e.target.value as FKind); setSel(null); }} style={ddl}>
          <option value="vat">{t('txf_vat')}</option>
          {/* "Withholding Tax" (generic) is filed via PND.1 / PND.3 / PND.53 below — no separate option. */}
          <option value="pnd1">PND.1</option>
          <option value="pnd3">PND.3</option>
          <option value="pnd53">PND.53</option>
          <option value="personal_income_tax">{t('tx_pit')}</option>
          <option value="sso">{t('tx_sso')}</option>
        </select>
        <select value={year} onChange={e => { setYear(Number(e.target.value)); setSel(null); }} style={ddl}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {/* Year-at-a-glance download: ALL clients × 12 months for the selected kind. */}
        <button onClick={async () => { setDl(true); try { await api.taxFilingsYearExport(year, kind); } catch (e: any) { alert(e.message); } finally { setDl(false); } }}
          disabled={dl} title={t('txf_year_dl_hint')}
          style={{ ...ddl, background: '#1D7C4D', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, opacity: dl ? 0.7 : 1 }}>
          <i className={`ti ti-${dl ? 'loader-2' : 'download'}`} /> {t('txf_year_dl')}
        </button>
      </div>

      {sel == null ? (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {overview.map(m => (
            <button key={m.month} onClick={() => setSel(m.month)}
              style={{ textAlign: 'left', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--bg-surface)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{MONTHS[m.month - 1]} {year}</span>
                {m.row_count > 0 && <span style={{ fontSize: 10.5, color: m.filed_count >= m.row_count ? '#15803d' : 'var(--text-tertiary)' }}>{m.filed_count}/{m.row_count} ✓</span>}
              </div>
              <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--accent)', marginTop: 6 }}>{money(m.total_tax)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{m.client_count} {t('txf_clients')} · {money(m.total_revenue)}</div>
            </button>
          ))}
        </div>

        {/* Per-company × 12-month detail — same layout as the downloadable Excel. */}
        {matrix.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('txf_detail_title')} · {year}</div>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{matrix.length} {t('txf_clients')}</span>
              <div style={{ flex: 1 }} />
              <button onClick={async () => { setDl(true); try { await api.taxFilingsYearExport(year, kind); } catch (e: any) { alert(e.message); } finally { setDl(false); } }}
                disabled={dl} title={t('txf_year_dl_hint')}
                style={{ ...ddl, background: '#1D7C4D', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, opacity: dl ? 0.7 : 1 }}>
                <i className={`ti ti-${dl ? 'loader-2' : 'file-spreadsheet'}`} /> Excel
              </button>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1040 }}>
                <thead><tr style={{ background: 'var(--c-gray-50)' }}>
                  <th style={{ ...th, width: 34, textAlign: 'center' }}>{t('tx_no')}</th>
                  <th style={{ ...th, minWidth: 168, position: 'sticky', left: 0, background: 'var(--bg-surface)', zIndex: 1 }}>{t('txf_company')}</th>
                  {MONTHS.map(mm => <th key={mm} style={{ ...th, textAlign: 'right', width: 68 }}>{mm}</th>)}
                  <th style={{ ...th, textAlign: 'right', width: 100, borderRight: 'none' }}>{t('txf_amount')}</th>
                </tr></thead>
                <tbody>
                  {matrix.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                      <td style={{ ...cell, fontWeight: 600, position: 'sticky', left: 0, background: 'var(--bg-surface)' }}>{r.name}</td>
                      {r.months.map((v, j) => <td key={j} style={{ ...cell, textAlign: 'right', color: v ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{v ? money(v) : '–'}</td>)}
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: 'var(--accent)', borderRight: 'none' }}>{money(r.total)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid var(--border-strong)', background: 'var(--c-gray-50)' }}>
                    <td style={cell} />
                    <td style={{ ...cell, fontWeight: 800, position: 'sticky', left: 0, background: 'var(--c-gray-50)' }}>{t('txf_total')}</td>
                    {Array.from({ length: 12 }).map((_, j) => <td key={j} style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{money(matrix.reduce((s, r) => s + (r.months[j] || 0), 0))}</td>)}
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: 'var(--accent)', borderRight: 'none' }}>{money(matrix.reduce((s, r) => s + (r.total || 0), 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setSel(null)} style={{ ...ddl, fontWeight: 600 }}><i className="ti ti-arrow-left" /> {MONTHS[sel - 1]} {year}</button>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>{taxLabel}</span>
            <div style={{ flex: 1 }} />
            {autoSync && <button onClick={refresh} disabled={busy} style={ddl}><i className={`ti ti-${busy ? 'loader-2' : 'refresh'}`} /> {t('txf_refresh')}</button>}
            <button onClick={addRow} style={ddl}>＋ {t('txf_add_row')}</button>
            <DlBtn onRun={() => api.taxFilingsExport(year, sel, kind)} style={{ ...ddl, background: '#1D7C4D', color: '#fff', border: 'none' }}><i className="ti ti-file-spreadsheet" /> Excel</DlBtn>
            <DlBtn onRun={() => api.taxFilingsPdf(year, sel, kind)} style={{ ...ddl, background: 'var(--accent)', color: '#fff', border: 'none' }}><i className="ti ti-file-type-pdf" /> PDF</DlBtn>
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            {kind === 'vat' ? (
              // VAT — the firm's PP.30 layout. Sale / output VAT / Purchase / input VAT come from scanned data;
              // Tax = output−input (computed); Brought-forward / Sur Charge / Fines are manual; Tax Payable computed.
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                <thead><tr style={{ background: 'var(--c-gray-50)' }}>
                  <th style={{ ...th, width: 32 }}>{t('tx_no')}</th>
                  <th style={{ ...th, minWidth: 150 }}>{t('txf_company')}</th>
                  <th style={{ ...th, width: 94, textAlign: 'right' }}>Sale</th>
                  <th style={{ ...th, width: 84, textAlign: 'right' }}>VAT 7%</th>
                  <th style={{ ...th, width: 94, textAlign: 'right' }}>Purchase</th>
                  <th style={{ ...th, width: 84, textAlign: 'right' }}>VAT 7%</th>
                  <th style={{ ...th, width: 90, textAlign: 'right' }}>Tax</th>
                  <th style={{ ...th, width: 96, textAlign: 'right' }}>Brought fwd</th>
                  <th style={{ ...th, width: 92, textAlign: 'right' }}>Sur Charge</th>
                  <th style={{ ...th, width: 80, textAlign: 'right' }}>Fines</th>
                  <th style={{ ...th, width: 102, textAlign: 'right' }}>Tax Payable</th>
                  <th style={{ ...th, width: 118 }}>Date</th>
                  <th style={{ ...th, width: 46, textAlign: 'center' }}>{t('txf_filed')}</th>
                  <th style={{ ...th, width: 34, textAlign: 'center' }} title={t('fd_title')}><i className="ti ti-paperclip" /></th>
                  <th style={{ ...th, width: 28, borderRight: 'none' }} />
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => {
                    const tax = num(r.out_vat) - num(r.in_vat);
                    const pay = Math.max(0, tax - num(r.brought_forward)) + num(r.surcharge) + num(r.fines);
                    const ni: React.CSSProperties = { ...inp, textAlign: 'right' };
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: r.filed ? '#F0FDF4' : undefined }}>
                        <td style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                        <td style={cell}><input value={r.client_name || ''} onChange={e => change(r.id, { client_name: e.target.value })} onBlur={() => saveRow(r.id)} style={inp} /></td>
                        <td style={cell}><input value={r.out_base ?? 0} onChange={e => change(r.id, { out_base: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={cell}><input value={r.out_vat ?? 0} onChange={e => change(r.id, { out_vat: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={cell}><input value={r.in_base ?? 0} onChange={e => change(r.id, { in_base: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={cell}><input value={r.in_vat ?? 0} onChange={e => change(r.id, { in_vat: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: tax < 0 ? '#15803d' : undefined }}>{money(tax)}</td>
                        <td style={cell}><input value={r.brought_forward ?? 0} onChange={e => change(r.id, { brought_forward: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={cell}><input value={r.surcharge ?? 0} onChange={e => change(r.id, { surcharge: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={cell}><input value={r.fines ?? 0} onChange={e => change(r.id, { fines: e.target.value })} onBlur={() => saveRow(r.id)} style={ni} inputMode="decimal" /></td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: pay < 0 ? '#15803d' : undefined }}>{money(pay)}</td>
                        <td style={cell}><input type="date" value={r.entry_date || ''} onChange={e => change(r.id, { entry_date: e.target.value })} onBlur={() => saveRow(r.id)} style={inp} /></td>
                        <td style={{ ...cell, textAlign: 'center' }}><input type="checkbox" checked={!!r.filed} onChange={e => toggleFiled(r.id, e.target.checked)} /></td>
                        <td style={{ ...cell, textAlign: 'center' }}>
                          <FilingDocsRowButton clientId={r.client_id} filing="vat" year={year} month={sel || 1} clientName={r.client_name || ''} />
                        </td>
                        <td style={{ ...cell, textAlign: 'center', borderRight: 'none' }}><button onClick={() => delRow(r.id)} title={t('delete')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button></td>
                      </tr>
                    );
                  })}
                  {!rows.length && <tr><td colSpan={15} style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)', padding: 20 }}>{t('txf_no_data')}</td></tr>}
                  <tr style={{ background: 'var(--c-gray-50)', fontWeight: 700 }}>
                    <td colSpan={2} style={{ ...cell, textAlign: 'right' }}>{t('txf_total')}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('out_base'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('out_vat'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('in_base'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('in_vat'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('out_vat') - sumOf('in_vat'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('brought_forward'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('surcharge'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('fines'))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(rows.reduce((s: number, r: any) => s + Math.max(0, num(r.out_vat) - num(r.in_vat) - num(r.brought_forward)) + num(r.surcharge) + num(r.fines), 0))}</td>
                    <td colSpan={4} style={{ ...cell, borderRight: 'none' }} />
                  </tr>
                </tbody>
              </table>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead><tr style={{ background: 'var(--c-gray-50)' }}>
                  <th style={{ ...th, width: 34 }}>{t('tx_no')}</th><th style={{ ...th, width: 110 }}>{t('tx_date')}</th>
                  <th style={{ ...th, width: 130 }}>{t('txf_doc')}</th><th style={{ ...th, minWidth: 180 }}>{t('txf_wht_name')}</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>{t('txf_revenue')}</th>
                  <th style={{ ...th, width: 70, textAlign: 'center' }}>{t('txf_rate')}</th>
                  <th style={{ ...th, width: 110, textAlign: 'right' }}>{taxLabel}</th>
                  <th style={{ ...th, width: 50, textAlign: 'center' }}>{t('txf_filed')}</th>
                  <th style={{ ...th, width: 30, borderRight: 'none' }} />
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: r.filed ? '#F0FDF4' : undefined }}>
                      <td style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                      <td style={cell}><input type="date" value={r.entry_date || ''} onChange={e => change(r.id, { entry_date: e.target.value })} onBlur={() => saveRow(r.id)} style={inp} /></td>
                      <td style={cell}><input value={r.doc_no || ''} onChange={e => change(r.id, { doc_no: e.target.value })} onBlur={() => saveRow(r.id)} style={inp} /></td>
                      <td style={cell}><input value={r.client_name || ''} onChange={e => change(r.id, { client_name: e.target.value })} onBlur={() => saveRow(r.id)} style={inp} /></td>
                      <td style={cell}><input value={r.revenue_amount ?? 0} onChange={e => change(r.id, { revenue_amount: e.target.value })} onBlur={() => saveRow(r.id)} style={{ ...inp, textAlign: 'right' }} inputMode="decimal" /></td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <input value={rateOf(r)} onChange={e => change(r.id, { _rate: e.target.value })} onBlur={() => saveRate(r.id)} style={{ ...inp, textAlign: 'right', width: 42, display: 'inline-block' }} inputMode="decimal" />%
                        {r.est ? <span title="AI-estimated" style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 4, padding: '1px 3px' }}>est</span> : null}
                      </td>
                      <td style={cell}><input value={r.tax_amount ?? 0} onChange={e => change(r.id, { tax_amount: e.target.value, _rate: undefined })} onBlur={() => saveRow(r.id)} style={{ ...inp, textAlign: 'right' }} inputMode="decimal" /></td>
                      <td style={{ ...cell, textAlign: 'center' }}><input type="checkbox" checked={!!r.filed} onChange={e => toggleFiled(r.id, e.target.checked)} /></td>
                      <td style={{ ...cell, textAlign: 'center', borderRight: 'none' }}><button onClick={() => delRow(r.id)} title={t('delete')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button></td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={9} style={{ ...cell, textAlign: 'center', color: 'var(--text-tertiary)', padding: 20 }}>{t('txf_no_data')}</td></tr>}
                  <tr style={{ background: 'var(--c-gray-50)', fontWeight: 700 }}>
                    <td colSpan={4} style={{ ...cell, textAlign: 'right' }}>{t('txf_total')}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('revenue_amount'))}</td>
                    <td style={cell} />
                    <td style={{ ...cell, textAlign: 'right' }}>{money(sumOf('tax_amount'))}</td>
                    <td colSpan={2} style={{ ...cell, borderRight: 'none' }} />
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Full-tab placeholder for tabs not built yet (Half-year Tax / EoY).
// ── Monthly Income/Expense Breakdown (owner 2026-08-17): client picker wrapper ──────────────────
function BreakdownTab() {
  const [client, setClient] = useState<Client | null>(null);
  if (!client) return <div style={{ flex: 1, overflow: 'auto' }}><ClientPicker onPick={setClient} /></div>;
  return <ExpenseBreakdownPanel client={client} onBack={() => setClient(null)} />;
}

// ── Half-year Tax: management P&L (owner spec "Mid year case 1 & 2", 2026-08-13) ────────────────
// A MANAGEMENT report, not a statutory statement. Actual months come from recorded invoices /
// payroll snapshots / PND.3; months without data fall back to the previous year's trial balance
// (4xxx = revenue, 5xxx = expenses) ÷ 12, and everything estimated is labelled as such.
// ── PND.51 filed documents (owner 2026-08-24) ──────────────────────────────────────────────────
// ภ.ง.ด.51 is filed ONCE for the year, so it lives here on the Half-year screen rather than in the
// Monthly tab's per-month Filing dropdown — the P&L above is literally the figure it reports.
// ── PND.51 worksheet — calc single point (Requirement 2, owner decisions 2026-08-25) ────────────
// Loss carried forward is deducted ONCE, in Section 1 (Section 2 merely displays it). SME uses the
// progressive schedule below; Special-Zone (RD 693/797) unlocks manual entry of item 6. All inputs
// persist per client-year in pnd51_worksheets; every computed figure derives from here.
const r2c = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
// SME progressive schedule — SINGLE POINT. Standard RD SME schedule as the default; swap the
// numbers confirmed by Nick 2026-08-25: 0–300k exempt, 300,001–3,000,000 at 15%, above at 20%,
// applied progressively (marginal brackets) to the half-year taxable base.
const SME_BRACKETS: Array<{ upTo: number; rate: number }> = [
  { upTo: 300000, rate: 0 },
  { upTo: 3000000, rate: 0.15 },
  { upTo: Infinity, rate: 0.20 },
];
const smeTax = (base: number): number => {
  let tax = 0; let prev = 0;
  for (const b of SME_BRACKETS) {
    if (base <= prev) break;
    tax += (Math.min(base, b.upTo) - prev) * b.rate;
    prev = b.upTo;
  }
  return r2c(tax);
};
const SME_LABEL = SME_BRACKETS.map((b, i) => {
  const lo = i === 0 ? 0 : SME_BRACKETS[i - 1].upTo;
  return `${lo.toLocaleString()}–${b.upTo === Infinity ? '∞' : b.upTo.toLocaleString()}: ${Math.round(b.rate * 100)}%`;
}).join(' · ');

function pnd51Calc(w: any) {
  const item3 = r2c((Number(w.s1_revenue) || 0) - (Number(w.s1_expenses) || 0));
  const loss = Number(w.s1_loss_cf) || 0;
  const exempt = w.s1_exempt_mode === 'full' ? Math.max(0, r2c(item3 - loss))
    : w.s1_exempt_mode === 'partial' ? (Number(w.s1_exempt_amount) || 0) : 0;
  const item6auto = r2c(item3 - loss - exempt);
  const overridden = !!w.s1_special_zone && w.s1_item6_override != null && w.s1_item6_override !== '';
  const item6 = overridden ? r2c(Number(w.s1_item6_override) || 0) : item6auto;
  const item7 = r2c(item6 / 2);
  const base = Math.max(0, item7);
  const mode = w.s2_rate_mode || 'general20';
  const statutory =
    mode === 'general20' ? r2c(base * 0.20)
    : mode === 'sme' ? smeTax(base)
    : mode === 'r15' ? r2c(base * 0.15)
    : mode === 'r10' ? r2c(base * 0.10)
    : mode === 'r8' ? r2c(base * 0.08)
    : mode === 'r5' ? r2c(base * 0.05)
    : mode === 'other' ? r2c(base * ((Number(w.s2_other_rate) || 0) / 100))
    : mode === 'r3' ? r2c((Number(w.s2_rev6m) || 0) * 0.03)
    : 0;
  const reductionCap = r2c(statutory * 0.5);
  const reductionApplied = Math.min(Number(w.s2_tax_reduction) || 0, reductionCap);
  const subtotal = r2c(statutory - (Number(w.s2_wht_credits) || 0) - reductionApplied);
  return { item3, exempt, item6auto, item6, item7, statutory, reductionCap, reductionApplied, subtotal,
    payable: Math.max(0, subtotal), credit: subtotal < 0 ? r2c(-subtotal) : 0, overridden };
}

// Prominent header toggle (owner 2026-08-25: "顶部显眼的地方加上复选框 Tax File Received") — the
// same per-client-year stamp the client list counts and colours by.
function ReceivedToggle({ clientId, year }: { clientId: string; year: number }) {
  const [rec, setRec] = useState<{ at: number; by: string } | null>(null);
  useEffect(() => {
    setRec(null);
    api.pnd51Get(clientId, year).then((r: any) => setRec({ at: Number(r.worksheet?.file_received_at) || 0, by: String(r.worksheet?.file_received_by || '') })).catch(() => setRec({ at: 0, by: '' }));
  }, [clientId, year]);
  const toggle = async (on: boolean) => {
    try {
      const r: any = await api.pnd51Received(clientId, year, on);
      setRec({ at: Number(r.worksheet?.file_received_at) || 0, by: String(r.worksheet?.file_received_by || '') });
    } catch { /* keep */ }
  };
  const on = (rec?.at || 0) > 0;
  return (
    <label title={on ? `${fmtDateTime(rec!.at)}${rec!.by ? ` · ${rec!.by}` : ''}` : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 800,
        border: `1.5px solid ${on ? '#F59E0B' : 'var(--border-strong)'}`, background: on ? '#FEF3C7' : 'var(--bg-app)', color: on ? '#92400E' : 'var(--text-secondary)' }}>
      <input type="checkbox" checked={on} disabled={rec == null} onChange={e => toggle(e.target.checked)} style={{ cursor: 'pointer' }} />
      Tax File Received{on && <span style={{ fontWeight: 600, fontSize: 11.5 }}>{new Date(rec!.at * 1000).toLocaleDateString(localeDate(), { timeZone: tz() })}</span>}
    </label>
  );
}

const PND51_DEFAULTS = {
  source: '', source_label: '', s1_revenue: 0, s1_expenses: 0, s1_loss_cf: 0, s1_exempt_mode: '',
  s1_exempt_amount: 0, s1_special_zone: '', s1_item6_override: null as number | null,
  s2_rate_mode: 'general20', s2_other_rate: 0, s2_rev6m: 0, s2_sme_tax: 0, s2_wht_credits: 0, s2_tax_reduction: 0,
};

function Pnd51Worksheet({ client, year, plData }: { client: Client; year: number; plData: any }) {
  const { t } = useTranslation();
  const [ws, setWs] = useState<any>({ ...PND51_DEFAULTS });
  // The confirmation email lives outside the input whitelist above, so its stamp is held separately.
  const [emailOpen, setEmailOpen] = useState(false);
  const [sentAt, setSentAt] = useState(0);

  const [loaded, setLoaded] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const pushTimer = useRef<any>(null);

  const load = () => api.pnd51Get(client.id, year).then((r: any) => {
    const w = r.worksheet || {};
    setWs({ ...PND51_DEFAULTS, ...Object.fromEntries(Object.entries(w).filter(([k]) => k in PND51_DEFAULTS)) });
    setSentAt(Number(w.estimate_email_at) || 0);
  }).catch(() => {});

  useEffect(() => {
    setLoaded(false);
    load().finally(() => setLoaded(true));
    return () => { if (pushTimer.current) clearTimeout(pushTimer.current); };
  }, [client.id, year]); // eslint-disable-line

  const push = (next: any) => {
    if (pushTimer.current) clearTimeout(pushTimer.current);
    pushTimer.current = setTimeout(() => {
      api.pnd51Save(client.id, { year, ...next }).then(() => { setSavedTick(true); setTimeout(() => setSavedTick(false), 1500); }).catch(() => {});
    }, 600);
  };
  const upd = (patch: any) => setWs((prev: any) => { const next = { ...prev, ...patch }; push(next); return next; });

  // Source import: choosing a source copies THAT card's figures into items 1–2 (as-is, no
  // annualising — owner 2026-08-25: the accountant adjusts) and stays editable afterwards.
  const sources: Array<{ key: string; label: string; ok: boolean; rev: number; exp: number }> = [
    { key: 'actual', label: `Actual P&L ${year}`, ok: !!plData?.totals && plData?.mode === 'actual', rev: plData?.totals?.revenue || 0, exp: plData?.totals?.expenses || 0 },
    { key: 'estimate', label: plData?.estimate ? `Estimated P&L (${plData.estimate.period || 'previous year'})` : 'Estimated P&L', ok: !!plData?.estimate, rev: plData?.estimate?.revenue || 0, exp: plData?.estimate?.expenses || 0 },
    { key: 'projection', label: `Full-Year Projection ${year}`, ok: !!plData?.projection, rev: plData?.projection?.revenue || 0, exp: plData?.projection?.expenses || 0 },
    { key: 'manual', label: 'Manual Input', ok: true, rev: 0, exp: 0 },
  ];
  const chooseSource = (sr: typeof sources[number]) => {
    if (!sr.ok) return;
    if (sr.key === 'manual') upd({ source: 'manual', source_label: 'Manual Input' });
    else upd({ source: sr.key, source_label: sr.label, s1_revenue: sr.rev, s1_expenses: sr.exp });
  };

  const c = pnd51Calc(ws);
  const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: '16px 18px' };
  const rowS: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', fontSize: 13 };
  const numIn: React.CSSProperties = { width: 170, textAlign: 'right', padding: '6px 9px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' };
  const autoBox: React.CSSProperties = { ...numIn, background: 'var(--c-gray-100)', border: '1px solid var(--border)', color: 'var(--text-secondary)', display: 'inline-block' };
  const secTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 800, color: '#15803D', marginBottom: 8, letterSpacing: '.01em' };
  const num = (v: any, onV: (n: number) => void, disabled = false) => (
    <NumCell value={Number(v) || 0} onValue={onV} onBlurSave={() => { /* push handled by upd */ }}
      style={{ ...numIn, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }} />
  );

  if (!loaded) return <div style={{ ...card, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}><i className="ti ti-loader" /></div>;

  return (
    <>
      {/* Source selection (Requirement 2 part A). The Tax-File-Received toggle lives in the page
          header now (owner 2026-08-25: prominent, top of the page). */}
      <div style={card}>
        <div style={{ ...secTitle, display: 'flex', alignItems: 'center' }}><i className="ti ti-database-import" style={{ marginRight: 6 }} />PND.51 Calculation Source
          <span style={{ flex: 1 }} />{savedTick && <span style={{ fontSize: 12, color: '#15803D', fontWeight: 700 }}><i className="ti ti-check" /> Saved</span>}</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {sources.map(sr => (
            <label key={sr.key} title={sr.ok ? `Revenue ${money(sr.rev)} · Expenses ${money(sr.exp)}` : 'No data for this source'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: sr.ok ? 'pointer' : 'not-allowed', opacity: sr.ok ? 1 : 0.45 }}>
              <input type="radio" name="pnd51src" disabled={!sr.ok} checked={ws.source === sr.key} onChange={() => chooseSource(sr)} />{sr.label}
            </label>
          ))}
        </div>
        {ws.source && <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--accent)' }}>PND.51 Calculation Source: {ws.source_label}</div>}
        {ws.source && ws.source !== 'manual' && <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--text-tertiary)' }}>Figures imported into items 1–2 below — still editable. Re-select the source to re-import.</div>}
      </div>

      {/* SECTION 1 */}
      <div style={card}>
        <div style={secTitle}>SECTION 1 — Half of Estimated Net Profit under Section 67 bis (1) of the Revenue Code <span style={{ float: 'right', color: 'var(--text-tertiary)', fontWeight: 400, fontSize: 11.5 }}>Unit: THB</span></div>
        <div style={rowS}><span style={{ flex: 1 }}>1. Estimated gross revenue before expenses for the current accounting period</span>{num(ws.s1_revenue, n => upd({ s1_revenue: n }))}</div>
        <div style={rowS}><span style={{ flex: 1 }}>2. Less: Estimated expenses for the current accounting period</span>{num(ws.s1_expenses, n => upd({ s1_expenses: n }))}</div>
        <div style={rowS}><span style={{ flex: 1, fontWeight: 600 }}>3. Estimated net profit / (loss)</span><span style={autoBox}>{money(c.item3)}</span></div>
        <div style={rowS}><span style={{ flex: 1 }}>4. Less: Tax losses carried forward (not exceeding 5 accounting periods)</span>{num(ws.s1_loss_cf, n => upd({ s1_loss_cf: n }))}</div>
        <div style={rowS}>
          <span style={{ flex: 1 }}>5. Less: Estimated tax-exempt net profit
            <label style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}><input type="checkbox" checked={ws.s1_exempt_mode === 'full'} onChange={e => upd({ s1_exempt_mode: e.target.checked ? 'full' : '' })} /> (1) Full amount</label>
            <label style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}><input type="checkbox" checked={ws.s1_exempt_mode === 'partial'} onChange={e => upd({ s1_exempt_mode: e.target.checked ? 'partial' : '' })} /> (2) Partial amount</label>
          </span>
          {ws.s1_exempt_mode === 'partial'
            ? num(ws.s1_exempt_amount, n => upd({ s1_exempt_amount: n }))
            : <span style={autoBox}>{money(c.exempt)}</span>}
        </div>
        <div style={rowS}><span style={{ flex: 1, fontWeight: 600 }}>6. Estimated taxable net profit / (loss)</span>
          {c.overridden || ws.s1_special_zone
            ? <NumCell value={ws.s1_item6_override == null ? c.item6auto : Number(ws.s1_item6_override)} onValue={n => upd({ s1_item6_override: n })} onBlurSave={() => {}} style={{ ...numIn, border: '1px solid #F59E0B', background: '#FFFBEB' }} />
            : <span style={autoBox}>{money(c.item6auto)}</span>}
        </div>
        <div style={rowS}><span style={{ flex: 1, fontWeight: 800 }}>7. Half of estimated taxable net profit / (loss)</span><span style={{ ...autoBox, fontWeight: 800, color: 'var(--text-primary)' }}>{money(c.item7)}</span></div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          If either status is selected, item 6 becomes manually editable:
          {([['693', 'Special Economic Development Zone — Royal Decree No. 693'], ['797', 'Special Economic Development Zone — Royal Decree No. 797']] as const).map(([v, t2]) => (
            <label key={v} style={{ display: 'block', marginTop: 4, cursor: 'pointer' }}>
              <input type="radio" name="pnd51zone" checked={ws.s1_special_zone === v}
                onClick={() => { if (ws.s1_special_zone === v) upd({ s1_special_zone: '', s1_item6_override: null }); }}
                onChange={() => upd({ s1_special_zone: v })} /> {t2}
            </label>
          ))}
        </div>
      </div>

      {/* SECTION 2 */}
      <div style={card}>
        <div style={secTitle}>SECTION 2 — Corporate Income Tax Payable and Calculation</div>
        <div style={rowS}><label style={{ flex: 1, cursor: 'pointer' }}><input type="checkbox" checked={ws.s2_rate_mode === 'general20'} onChange={() => upd({ s2_rate_mode: 'general20' })} /> 1. General rate at 20%</label>
          <span style={autoBox}>{ws.s2_rate_mode === 'general20' ? money(c.statutory) : '—'}</span></div>
        <div style={rowS}><label style={{ flex: 1, cursor: 'pointer' }}><input type="checkbox" checked={ws.s2_rate_mode !== 'general20'} onChange={() => upd({ s2_rate_mode: 'sme' })} /> 2. Preferential tax rate</label></div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '2px 0 4px 22px', fontSize: 12.5 }}>
          {([['sme', 'SMEs'], ['r15', '15%'], ['r10', '10%'], ['r8', '8%'], ['r5', '5%'], ['other', 'Other rate'], ['r3', '3% rate']] as const).map(([v, t2]) => (
            <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="radio" name="pnd51rate" checked={ws.s2_rate_mode === v} onChange={() => upd({ s2_rate_mode: v })} />{t2}
            </label>
          ))}
          {ws.s2_rate_mode === 'other' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><NumCell value={Number(ws.s2_other_rate) || 0} onValue={n => upd({ s2_other_rate: n })} onBlurSave={() => {}} style={{ ...numIn, width: 70 }} />%</span>}
        </div>
        {ws.s2_rate_mode === 'sme' && <div style={{ margin: '0 0 6px 22px', fontSize: 11.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '4px 10px', display: 'inline-block' }}>SME progressive schedule: {SME_LABEL}</div>}
        <div style={rowS}><span style={{ flex: 1, opacity: ws.s2_rate_mode === 'r3' ? 1 : 0.5 }}>Revenue before expenses for the first 6 months (tax base for the 3% rate)</span>{num(ws.s2_rev6m, n => upd({ s2_rev6m: n }), ws.s2_rate_mode !== 'r3')}</div>
        <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />
        <div style={rowS}><span style={{ flex: 1 }}>Net profit / (loss) — from Section 1 item 7</span><span style={autoBox}>{money(c.item7)}</span></div>
        <div style={rowS}><span style={{ flex: 1, color: 'var(--text-tertiary)' }}>Tax losses carried forward <i>(display only — already deducted in Section 1 item 4)</i></span><span style={{ ...autoBox, opacity: 0.6 }}>{money(ws.s1_loss_cf)}</span></div>
        <div style={rowS}><span style={{ flex: 1, fontWeight: 700 }}>Taxable Net Profit (Total)</span><span style={{ ...autoBox, fontWeight: 700 }}>{money(c.item7)}</span></div>
        <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', margin: '4px 0' }}>Tax Deductions</div>
        <div style={rowS}><span style={{ flex: 1 }}>Withholding tax and tax credits</span>{num(ws.s2_wht_credits, n => upd({ s2_wht_credits: n }))}</div>
        <div style={rowS}><span style={{ flex: 1 }}>Tax reduction eligible (not exceeding 50% of the statutory tax{c.reductionCap ? ` — cap ${money(c.reductionCap)}` : ''})</span>{num(ws.s2_tax_reduction, n => upd({ s2_tax_reduction: n }))}</div>
        {Number(ws.s2_tax_reduction) > c.reductionCap && <div style={{ fontSize: 11.5, color: '#C0392B', marginLeft: 2 }}>Entered reduction exceeds the 50% cap — only {money(c.reductionApplied)} is applied.</div>}
        <div style={rowS}><span style={{ flex: 1, fontWeight: 600 }}>Subtotal</span><span style={autoBox}>{money(c.subtotal)}</span></div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, background: '#E8F6EE', border: '1px solid #9BD8B3', borderRadius: 10, padding: '10px 14px' }}>
          <span style={{ fontSize: 14.5, fontWeight: 800, color: '#166534', flex: 1 }}>Corporate Income Tax Payable</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#166534', fontVariantNumeric: 'tabular-nums' }}>{money(c.payable)}</span>
        </div>
        {c.credit > 0 && <div style={{ marginTop: 6, fontSize: 12, color: '#B45309' }}>Result is negative ({money(c.credit)}) — treated as credit / refund, not tax payable.</div>}
        {/* The estimate has to be agreed by the client before we file, so the worksheet is where the
            confirmation email starts (owner 2026-08-28). `check` carries the number on screen so the
            server refuses to build the email if its own recalculation disagrees. */}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => setEmailOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-mail" style={{ fontSize: 15 }} />{t('hy_email_btn')}
          </button>
          {sentAt > 0 && (
            <span style={{ fontSize: 11.5, color: '#166534', background: '#E8F6EE', border: '1px solid #9BD8B3', borderRadius: 999, padding: '2px 10px', fontWeight: 700 }}>
              <i className="ti ti-check" style={{ marginRight: 4 }} />{t('hy_email_sent_at', { when: new Date(sentAt * 1000).toLocaleString(localeDate(), { timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) })}
            </span>
          )}
        </div>
      </div>
      {emailOpen && <Pnd51EmailComposer clientId={client.id} year={year} check={c.payable}
        onClose={() => setEmailOpen(false)} onSent={() => { setEmailOpen(false); load(); }} />}
    </>
  );
}

// An amended return produces a second (and third) submission, so the normal filing and every
// Additional Filing No. n each get their own card, exactly as the monthly returns do.
function Pnd51Docs({ clientId, year }: { clientId: string; year: number }) {
  // `top` is the highest submission number on screen; the cards are always 0…top with no gaps,
  // because addNext only ever offers top+1.
  const [top, setTop] = useState(0);
  // The server derives filingNos from documents that EXIST, so a submission the operator just
  // opened — or one whose files they deleted — is unknown to it. Taking the server list verbatim
  // made that card vanish under them the moment any upload refreshed the list (review 2026-08-24),
  // so a refresh may only ever RAISE the ceiling, never lower it.
  const load = useCallback(() => {
    api.filingDocsList(clientId, 'pnd51', year, 0, 0)
      .then((d: any) => setTop(t => Math.max(t, ...((d?.filingNos as number[]) || [0]))))
      .catch(() => { /* keep what is on screen */ });
  }, [clientId, year]);
  // Switching client or year is a different set of submissions — start from the normal filing again,
  // or an empty card would follow the operator to a client that never had an amended return.
  useEffect(() => { setTop(0); }, [clientId, year]);
  useEffect(() => { load(); }, [load]);
  const nos = Array.from({ length: top + 1 }, (_, i) => i);
  const addNext = () => setTop(t => t + 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {nos.map(n => (
        <div key={n}>
          {n > 0 && (
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '4px 10px', display: 'inline-block', marginBottom: 6 }}>
              Additional filing No. {n}
            </div>
          )}
          <FilingDocsCard clientId={clientId} filing="pnd51" year={year} month={0} monthName="" filingNo={n} onChanged={load} />
          <PenaltyCard clientId={clientId} kind="pnd51" year={year} month={0} filingNo={n} />
        </div>
      ))}
      <div>
        <button onClick={addNext}
          style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px dashed var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)' }}
          title="An amended PND.51 is a separate submission — it gets its own receipt and form rather than overwriting the original.">
          <i className="ti ti-plus" style={{ marginRight: 5 }} />Add additional filing
        </button>
      </div>
    </div>
  );
}

// ── Half-year client list (owner 2026-08-25) ────────────────────────────────
// All monthly + annual clients, A→Z, with a YEAR selector (defaults to this year — the PND.51
// documents are stored per year). A client that already uploaded Filed-return documents for the
// selected year gets a PINK row, so the remaining work is visible at a glance.
function HalfYearClientPicker({ year, setYear, onPick }: { year: number; setYear: (y: number) => void; onPick: (c: Client) => void }) {
  const { t } = useTranslation();
  const nowY = new Date().getFullYear();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [flt, setFlt] = useState<'all' | 'none' | 'received' | 'estimate' | 'filed'>('all');
  useEffect(() => {
    const h = setTimeout(() => {
      setLoading(true);
      api.taxHalfyearClients(q, year).then((d: any) => setRows(d.rows || [])).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(h);
  }, [q, year]);
  // Received and Uploaded are INDEPENDENT flags (owner 2026-08-25: ticking Tax File Received on an
  // already-Filed client must still bump the Received count) — a client can sit in both chips at
  // once. Only the row COLOUR keeps the filed-over-received priority. Not Received stays "no flag
  // at all" so the remaining-work count is unchanged by filings.
  const isFiled = (c: any) => !!c.pnd51_filed;
  const isReceived = (c: any) => Number(c.file_received_at) > 0;
// Fourth signal (owner 2026-08-28): the PND.51 estimate has been emailed to the client for
// confirmation. Independent of Received/Filed — it only says the client has been asked.
const isEstimateSent = (c: any) => Number(c.estimate_email_at) > 0;
  const statusOf = (c: any) => (isFiled(c) ? 2 : isReceived(c) ? 1 : 0);
  const counts = { all: rows.length, none: rows.filter(c => !isFiled(c) && !isReceived(c)).length, received: rows.filter(isReceived).length, estimate: rows.filter(isEstimateSent).length, filed: rows.filter(isFiled).length };
  const shown = flt === 'all' ? rows : rows.filter(c => flt === 'none' ? !isFiled(c) && !isReceived(c) : flt === 'received' ? isReceived(c) : flt === 'estimate' ? isEstimateSent(c) : isFiled(c));
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('tx_pick_client')}</div>
        <div style={{ flex: 1 }} />
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '7px 11px', fontSize: 13, fontWeight: 700, borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
          {Array.from({ length: 4 }, (_, i) => nowY + 1 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('tx_search_client')} name="hy-client-filter" autoComplete="off"
          style={{ width: '100%', padding: '11px 12px 11px 36px', fontSize: 14, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {([['all', 'All'], ['none', 'Not Received'], ['received', 'Tax File Received'], ['estimate', 'Estimate Sent'], ['filed', 'Documents Uploaded']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setFlt(k)}
            style={{ padding: '5px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${flt === k ? 'var(--accent)' : 'var(--border)'}`,
              background: flt === k ? 'var(--bg-active)' : k === 'received' ? '#FFFBEB' : k === 'filed' ? '#FDF2F8' : k === 'estimate' ? '#E8F6EE' : 'transparent',
              color: flt === k ? 'var(--accent)' : k === 'received' ? '#92400E' : k === 'filed' ? '#BE185D' : k === 'estimate' ? '#166534' : 'var(--text-secondary)' }}>
            {label} · {counts[k]}
          </button>
        ))}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : shown.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('tx_no_client')}</div>
          : shown.map(c => {
            const st = statusOf(c);
            const bg = st === 2 ? 'rgba(236,72,153,0.12)' : st === 1 ? 'rgba(245,158,11,0.14)' : 'transparent';
            return (
              <div key={c.id} onClick={() => onPick(c)}
                title={[isFiled(c) ? t('hy_filed_hint', { year }) : '', isReceived(c) ? `Tax file received ${fmtDateTime(Number(c.file_received_at))}` : ''].filter(Boolean).join(' · ') || undefined}
                style={{ padding: '11px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10, background: bg }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = bg)}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{c.company_name || c.client_name}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  {isFiled(c) && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#BE185D', background: '#FCE7F3', border: '1px solid #F9A8D4', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>{t('hy_filed_chip')}</span>}
                  {isReceived(c) && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBD38D', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>Received</span>}
                  {isEstimateSent(c) && <span title={`Estimate emailed ${fmtDateTime(Number(c.estimate_email_at))}`} style={{ fontSize: 10.5, fontWeight: 800, color: '#166534', background: '#E8F6EE', border: '1px solid #9BD8B3', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>{t('hy_estimate_chip')}</span>}
                  <TaxIcons c={c} />
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.tax_id || '—'}</span>
                </span>
              </div>
            );
          })}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 8, textAlign: 'center' }}>{shown.length} / {rows.length} clients · {year}</div>
    </div>
  );
}

// Half-year tab: Companies file PND.51 (the existing panel), individuals file PND.94.
function HalfYearTab() {
  const { t } = useTranslation();
  const [sub, setSub] = useState<'pnd51' | 'pnd94'>('pnd51');
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 4, padding: '8px 22px 0', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {([['pnd51', `${t('atx_sub_companies')} — PND.51`], ['pnd94', `${t('atx_sub_individuals')} — PND.94`]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setSub(k)}
            style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: sub === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `2.5px solid ${sub === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
        ))}
      </div>
      {sub === 'pnd51' ? <HalfYearPanel /> : <AnnualPanel key="pnd94" kind="pnd94" />}
    </div>
  );
}

function HalfYearPanel() {
  const { t } = useTranslation();
  const [client, setClient] = useState<Client | null>(null);
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(6);                     // mid-year context: default H1
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [idCopied, setIdCopied] = useState(false);   // tax-ID copy feedback (owner 2026-08-25)

  useEffect(() => {
    if (!client) { setData(null); return; }
    let dead = false;
    setLoading(true);
    api.plReport(client.id, year, from, to)
      .then((d: any) => { if (!dead) setData(d); })
      .catch(() => { if (!dead) setData(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [client, year, from, to]);

  if (!client) return <div style={{ flex: 1, overflow: 'auto' }}><HalfYearClientPicker year={year} setYear={setYear} onPick={setClient} /></div>;

  const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const PRESETS: Array<[string, number, number]> = [
    ['Q1', 1, 3], ['Q2', 4, 6], ['Q3', 7, 9], ['Q4', 10, 12], ['H1', 1, 6], ['H2', 7, 12], [t('pl_full_year'), 1, 12],
  ];
  const sel: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: '16px 18px' };
  const hRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0' };
  const lbl: React.CSSProperties = { color: 'var(--text-secondary)' };
  const val: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text-primary)' };
  const estAmber = { background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' };

  const est = data?.estimate;
  const proj = data?.projection;
  const totals = data?.totals;

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: `18px ${PAGE_INSET}px` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => setClient(null)} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
          <i className="ti ti-arrow-left" style={{ marginRight: 5 }} />{t('tx_change_client')}</button>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{data?.client?.name || ''}</span>
        {!!client.tax_id && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'ui-monospace, monospace', letterSpacing: '.02em' }}>{client.tax_id}</span>
            <button onClick={() => { navigator.clipboard.writeText(client.tax_id).catch(() => {}); setIdCopied(true); setTimeout(() => setIdCopied(false), 2000); }}
              title={t('jb_copy')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: '1px solid var(--border-strong)', background: idCopied ? '#E8F6EE' : 'var(--bg-app)', color: idCopied ? '#166534' : 'var(--text-secondary)', borderRadius: 7, padding: '3px 8px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
              <i className={`ti ${idCopied ? 'ti-check' : 'ti-copy'}`} style={{ fontSize: 12.5 }} />{idCopied ? t('copied', { defaultValue: 'Copied' }) : t('jb_copy')}
            </button>
          </span>
        )}
        <ReceivedToggle clientId={client.id} year={year} />
        <div style={{ flex: 1 }} />
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
          {Array.from({ length: 4 }, (_, i) => nowY + 1 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={from} onChange={e => { const v = Number(e.target.value); setFrom(v); if (v > to) setTo(v); }} style={sel}>
          {MONTH_ABBR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
        <select value={to} onChange={e => { const v = Number(e.target.value); setTo(v); if (v < from) setFrom(v); }} style={sel}>
          {MONTH_ABBR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
        {PRESETS.map(([label, f, tt]) => (
          <button key={label} onClick={() => { setFrom(f); setTo(tt); }}
            style={{ padding: '5px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${from === f && to === tt ? 'var(--accent)' : 'var(--border)'}`,
              background: from === f && to === tt ? 'var(--bg-active)' : 'transparent',
              color: from === f && to === tt ? 'var(--accent)' : 'var(--text-secondary)' }}>{label}</button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
      : !data ? <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pl_load_failed')}</div>
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1080 }}>

          {data.mode === 'empty' && (
            <div style={{ ...card, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pl_no_data')}</div>
          )}

          {data.mode !== 'empty' && data.missingPayroll?.length > 0 && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 13px' }}>
              <i className="ti ti-alert-triangle" style={{ marginTop: 1 }} />
              <span>{t('pl_missing_payroll', { months: data.missingPayroll.map((m: number) => MONTH_ABBR[m - 1]).join(', ') })}</span>
            </div>
          )}

          {data.mode === 'actual' && totals && (
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}>
                <i className="ti ti-report-money" style={{ marginRight: 6 }} />{t('pl_actual_title', { from: MONTH_ABBR[from - 1], to: MONTH_ABBR[to - 1], year })}
              </div>
              <div style={hRow}><span style={lbl}>{t('pl_revenue')}</span><span style={val}>{money(totals.revenue)}</span></div>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              {/* Zero rows hidden (owner 2026-08-16) — only expenses that exist are listed, then the
                  Total expenses subtotal, then the net. */}
              {!!totals.purchases && <div style={hRow}><span style={lbl}>{t('pl_purchases')}</span><span style={val}>−{money(totals.purchases)}</span></div>}
              {!!totals.salaries && <div style={hRow}><span style={lbl}>{t('pl_salaries')}</span><span style={val}>−{money(totals.salaries)}</span></div>}
              {!!totals.sso && <div style={hRow}><span style={lbl}>{t('pl_employer_sso')}</span><span style={val}>−{money(totals.sso)}</span></div>}
              {!!totals.pnd3 && <div style={hRow}><span style={lbl}>{t('pl_pnd3')}</span><span style={val}>−{money(totals.pnd3)}</span></div>}
              {!!(totals as any).rent53 && <div style={hRow}><span style={lbl}>{t('pl_rent53')}</span><span style={val}>−{money((totals as any).rent53)}</span></div>}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <div style={hRow}><span style={{ ...lbl, fontWeight: 700 }}>{t('pl_expenses_total')}</span><span style={{ ...val, fontWeight: 700 }}>−{money((totals as any).expenses ?? (totals.purchases + totals.salaries + totals.sso + totals.pnd3))}</span></div>
              <div style={{ borderTop: '2px solid var(--text-primary)', margin: '6px 0 2px' }} />
              <div style={{ ...hRow, fontSize: 14.5 }}>
                <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{t('pl_net')}</span>
                <span style={{ ...val, fontWeight: 800, color: totals.net >= 0 ? '#1D7C4D' : '#B42318' }}>{money(totals.net)}</span>
              </div>
            </div>
          )}

          {(data.mode === 'estimate' || (proj && proj.estimatedMonths > 0)) && est && (
            <div style={{ ...card, ...estAmber }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase', marginBottom: 8 }}>
                <i className="ti ti-wand" style={{ marginRight: 6 }} />{t('pl_est_label', { period: est.period })}
              </div>
              <div style={hRow}><span>{t('pl_est_revenue')}</span><span style={{ ...val, color: 'inherit' }}>{money(est.revenue)}</span></div>
              <div style={hRow}><span>{t('pl_est_expenses')}</span><span style={{ ...val, color: 'inherit' }}>−{money(est.expenses)}</span></div>
              <div style={{ borderTop: '1px solid #FDE68A', margin: '4px 0' }} />
              <div style={{ ...hRow, fontWeight: 800 }}><span>{t('pl_est_profit')}</span><span style={{ ...val, color: 'inherit', fontWeight: 800 }}>{money(est.profit)}</span></div>
            </div>
          )}

          {proj && (
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                <i className="ti ti-chart-line" style={{ marginRight: 6 }} />{t('pl_proj_title', { year })}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>
                {t('pl_proj_mix', { actual: proj.actualMonths, estimated: proj.estimatedMonths })}
                {proj.basedOn ? ` · ${t('pl_est_label', { period: proj.basedOn })}` : ''}
              </div>
              {/* month strip: which months are actual (green) vs estimated (amber) */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                {proj.months.map((m: any) => (
                  <span key={m.month} title={`${MONTH_ABBR[m.month - 1]}: ${t(m.source === 'actual' ? 'pl_src_actual' : 'pl_src_estimated')}`}
                    style={{ fontSize: 10.5, fontWeight: 700, padding: '3px 7px', borderRadius: 6,
                      background: m.source === 'actual' ? '#E7F6EE' : '#FFFBEB',
                      color: m.source === 'actual' ? '#1D7C4D' : '#92400E',
                      border: `1px solid ${m.source === 'actual' ? '#B7E4CC' : '#FDE68A'}` }}>
                    {MONTH_ABBR[m.month - 1]}</span>
                ))}
              </div>
              <div style={hRow}><span style={lbl}>{t('pl_revenue')}</span><span style={val}>{money(proj.revenue)}</span></div>
              <div style={hRow}><span style={lbl}>{t('pl_expenses_total')}</span><span style={val}>−{money(proj.expenses)}</span></div>
              <div style={{ borderTop: '2px solid var(--text-primary)', margin: '6px 0 2px' }} />
              <div style={{ ...hRow, fontSize: 14.5 }}>
                <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{t('pl_proj_net')}</span>
                <span style={{ ...val, fontWeight: 800, color: proj.net >= 0 ? '#1D7C4D' : '#B42318' }}>{money(proj.net)}</span>
              </div>
              {proj.incomplete && <div style={{ marginTop: 8, fontSize: 12, color: '#B42318' }}><i className="ti ti-alert-circle" style={{ marginRight: 5 }} />{t('pl_proj_incomplete')}</div>}
            </div>
          )}

          {(est || proj) && (
            <div style={{ fontSize: 11.5, lineHeight: 1.65, color: 'var(--text-tertiary)', border: '1px dashed var(--border-strong)', borderRadius: 10, padding: '10px 14px' }}>
              <i className="ti ti-info-circle" style={{ marginRight: 5 }} />{t('pl_disclaimer')}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {/* Half-year: the period is the year itself, so month 0. */}
        <ClientPaidStamp clientId={client.id} year={year} month={0} taxKey="pnd51" />
        <Pnd51Worksheet client={client} year={year} plData={data} />
        <Pnd51Docs clientId={client.id} year={year} />
      </div>
    </div>
  );
}

function TaxTabPlaceholder({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, color: 'var(--text-tertiary)' }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: 'var(--bg-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}><i className="ti ti-tools" style={{ fontSize: 28 }} aria-hidden /></div>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</div>
      <div style={{ fontSize: 13 }}>{t('under_development')}</div>
    </div>
  );
}

type TaxTab = 'monthly' | 'halfyear' | 'breakdown' | 'eoy' | 'property' | 'filing';

// ── Whose books are on this screen (owner 2026-09-25) ───────────────────────
// This page used to open with a CITY picker — Samui or Bangkok — because Bangkok was a second
// system reached through the Tax Hub proxy. The firm is organised by COMPANY, not by city: the
// main menu already carries the one company switcher, and every tax route has filtered by the
// active company since the multi-company work landed (2026-09-22). A second picker here was a
// competing answer to "whose books am I in", so it is gone: the page simply shows the company
// the rest of the system is showing, named in the header so it can never be a guess.
//
// The other office's region and the proxy to it are gone too (owner Q19, 2026-10-03): this page
// only ever works on this system's own books (server/src/services/taxHub.ts).

export default function Tax() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // The active section lives in the URL (?section=…) so a page refresh stays on the same tab.
  const validTab = (v: string | null): TaxTab => (['monthly', 'halfyear', 'breakdown', 'eoy', 'property', 'filing'].includes(v || '') ? (v as TaxTab) : 'monthly');
  const [tab, setTab] = useState<TaxTab>(() => validTab(params.get('section')));
  const selectTab = (k: TaxTab) => { setTab(k); setParams(p => { const n = new URLSearchParams(p); n.set('section', k); return n; }, { replace: true }); };
  // The books belong to the active company. Named from /api/me's own list so the wording matches
  // the main menu exactly; switching company reloads the whole app, so nothing here goes stale.
  // The tab tree is keyed by it anyway — a company that changed underneath must never leave one
  // firm's figures held in a panel's state.
  const { me } = useStore();
  const company = String(getActiveCompanyId() || (me as any)?.activeCompanyId || (me as any)?.companyId || '');
  const companyName = ((me as any)?.companies || []).find((c: any) => String(c.id) === company)?.name
    || (me as any)?.companyName || '';
  const tabs: [TaxTab, string][] = [['monthly', t('tx_tab_monthly')], ['halfyear', t('tx_tab_halfyear')], ['breakdown', 'Income Statement'], ['eoy', t('tx_tab_eoy')], ['property', t('tx_property_tax')], ['filing', t('fc_tab')]];
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 16px 0', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {/* The auditor login's whole app is this page (Bugify #3898b242), so its logo opens the main menu —
            SLF Tax, the language switch, Sign out — where everyone else's goes back to the dashboard. */}
        {me?.role === 'auditor' ? (
          <div style={{ alignSelf: 'center', marginBottom: 6 }}><AuditorMenu /></div>
        ) : (
          <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', alignSelf: 'center', marginBottom: 6 }}>
            <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
          </button>
        )}
        <b style={{ fontSize: 14.5, color: 'var(--text-primary)', marginBottom: 6, whiteSpace: 'nowrap' }}>{t('th_region_title')}</b>
        {/* Whose books these are. Not a second switcher — the company is chosen once, in the main
            menu; clicking here goes back there, which is the only place it changes. */}
        <button onClick={() => navigate('/')} title={t('co_switch_hint')} aria-label={t('co_switch')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, padding: '6px 11px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <i className="ti ti-building" style={{ fontSize: 13, color: 'var(--accent)' }} aria-hidden />
          {companyName || t('co_switch')}
        </button>
        <div style={{ display: 'flex', gap: 4 }}>
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => selectTab(k)}
              style={{ padding: '9px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'transparent', color: tab === k ? 'var(--accent)' : 'var(--text-tertiary)', borderBottom: `3px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>{label}</button>
          ))}
        </div>
      </header>
      <React.Fragment key={company}>
      {tab === 'monthly' ? <TaxView />
        : tab === 'breakdown' ? <BreakdownTab />
        : tab === 'property' ? <PropertyTax />
        : tab === 'filing' ? <FilingCenter />
        : tab === 'halfyear' ? <HalfYearTab />
        : <EoYPanel />}
      </React.Fragment>
    </div>
  );
}

// ── Filing Status: 5 kinds × 12 months, one glance (owner 2026-08-05) ────────
// Same data the client sees on their portal (shared builder server-side), so "complete" here is
// exactly "complete" there. Green = filed + documents on file; amber = marked filed but documents
// missing; blue chip only = documents uploaded but the month not marked filed; dot = nothing yet.
function FilingStatusPanel({ clientId, year }: { clientId: string; year: number }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    setBusy(true);
    api.clientFilingStatus(clientId, year).then((d: any) => setRows(d.rows || [])).catch(() => setRows([])).finally(() => setBusy(false));
  }, [clientId, year]);
  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see th at the top of the file). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see th there too).
  const th: React.CSSProperties = { textAlign: 'center', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 6px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { textAlign: 'center', fontSize: 12, padding: '8px 4px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  if (busy) return <div style={{ padding: 30, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>{t('tx_fs_hint')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>{t('tx_fs_filing')}</th>
            {MONTHS.map(m => <th key={m} style={th}>{m.slice(0, 3)}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} style={{ opacity: r.applicable ? 1 : 0.38 }} title={r.applicable ? undefined : t('tx_fs_na')}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.label}{!r.applicable && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-tertiary)' }}>{t('tx_fs_na_tag')}</span>}</td>
                {r.months.map((m: any) => {
                  const complete = m.filed && m.docs.length > 0;
                  return (
                    // An ANNUAL return (PND.51) is filed once for the year, so it spans the whole row
                    // instead of being planted in one month — which month a half-year return "belongs
                    // to" is a tax judgement, and this grid is shown to the client (owner 2026-08-24).
                    <td key={m.month} colSpan={r.annual ? 12 : 1} style={{ ...td, background: complete ? '#E8F6EE' : undefined }}>
                      {r.annual && <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', marginBottom: 2 }}>{m.name}</div>}
                      {complete ? <i className="ti ti-circle-check-filled" style={{ color: '#1D9E75', fontSize: 16 }} title={t('tx_fs_complete')} />
                        : m.filed ? <i className="ti ti-circle-check" style={{ color: '#D97706', fontSize: 16 }} title={t('tx_fs_filed_nodocs')} />
                        : m.docs.length ? null
                        : <span style={{ color: 'var(--border-strong)' }}>·</span>}
                      {m.docs.map((d: any) => (
                        <button key={d.id} title={d.filename} onClick={() => api.openFilingDoc(d.id, true).catch(() => {})}
                          style={{ display: 'block', margin: '3px auto 0', padding: '1px 7px', border: '1px solid var(--border-strong)', borderRadius: 999, background: 'var(--bg-app)', color: 'var(--accent)', fontWeight: 700, fontSize: 10.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {d.slot === 'form' ? t('tx_fs_form') : d.slot === 'attached' ? t('tx_fs_attached') : t('tx_fs_receipt')}{d.filing > 0 ? ` A${d.filing}` : ''}
                        </button>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 10 }}>{t('tx_fs_legend')}</div>
    </div>
  );
}

// ── Submission chips: Normal / Additional No. n (additional filings, 2026-08-05) ─────────────────
// One month can hold several submissions to the Revenue Department. "All" is the working view
// (everything on one screen); picking a submission filters the lines, routes new lines and the
// filed-document slots to it, and scopes the Excel download. 🔒 = that submission is marked filed.
function FilingChips({ clientId, kind, year, month, value, onChange, refreshKey }: {
  clientId: string; kind: string; year: number; month: number;
  value: 'all' | number; onChange: (v: 'all' | number) => void; refreshKey?: any;
}) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<any>(null);
  useEffect(() => {
    let live = true;
    api.taxFilingsOf(clientId, kind, year, month).then((d: any) => { if (live) setInfo(d); }).catch(() => {});
    return () => { live = false; };
  }, [clientId, kind, year, month, refreshKey]);
  const filings: any[] = info?.filings || [{ filing_no: 0, filed: false, lines: 0 }];
  const maxFn = filings.reduce((m: number, f: any) => Math.max(m, f.filing_no), 0);
  const chip = (key: string, label: React.ReactNode, active: boolean, on: () => void, title?: string) => (
    <button key={key} onClick={on} title={title} style={{ padding: '4px 11px', borderRadius: 999, border: '1px solid', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
      borderColor: active ? 'var(--accent)' : 'var(--border-strong)', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '2px 0 10px' }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('tx_fl_label')}</span>
      {chip('all', t('tx_fl_all'), value === 'all', () => onChange('all'))}
      {filings.map((f: any) => chip(String(f.filing_no),
        <>{f.filing_no === 0 ? t('tx_fl_normal') : `${t('tx_fl_add')} ${f.filing_no}`}{f.filed ? ' 🔒' : ''}{f.lines ? ` · ${f.lines}` : ''}</>,
        value === f.filing_no, () => onChange(f.filing_no), f.filed ? t('tx_fl_locked_tip') : undefined))}
      {chip('new', `+ ${t('tx_fl_add')} ${maxFn + 1}`, value === maxFn + 1 && !filings.some((f: any) => f.filing_no === maxFn + 1), () => onChange(maxFn + 1), t('tx_fl_new_tip'))}
    </div>
  );
}
